"use client";

// Libraries
import { Link } from "@/i18n/navigation";
import Image from "next/image";
import { signIn, useSession } from "next-auth/react";
import { sendGTMEvent } from "@next/third-parties/google";
import posthog from "posthog-js";
import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { AnimatePresence, motion } from "motion/react";
import { useLocale, useTranslations } from "next-intl";
import {
    GoogleReCaptchaProvider,
    useGoogleReCaptcha,
} from "react-google-recaptcha-v3";
import ReCAPTCHA from "react-google-recaptcha";

// Stripe prices config
import { STRIPE_PRICES } from "@/lib/stripe-prices";

// Reps : deux interrupteurs produit décident des avantages annoncés (jamais
// promettre ce qui n'est pas en ligne : app absente de l'App Store, chat
// BeBot restreint au palier Pro)
import { isRepsAppStoreLive } from "@/lib/reps/mobileApp";
import { repsAssistantIncludedInPlus } from "@/lib/reps/access";

// Components
import Button from "@/components/ui/Button/Button";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import PixelIcon from "@/components/ui/PixelIcon/PixelIcon";
import useInfluencerCampaign from "@/hooks/useInfluencerCampaign";
import useCountryOptions from "@/hooks/useCountryOptions";
import {
    getPostalCodeError,
    getPostalCodeField,
    normalizePostalCode,
} from "@/lib/billingAddress";
import {
    AFRICA_CHECKOUT_FALLBACK,
    isAfricaCheckoutType,
} from "@/lib/africaCountries";
import CrispChatbox from "@/components/ui/Crisp/Crisp";
import { Elements } from "@stripe/react-stripe-js";
import CheckoutForm from "@/components/ui/CheckoutForm/CheckoutForm";
import {
    WINBACK_CHECKOUT_TYPE,
    getWinbackPrice,
} from "@/lib/winbackOfferConfig";
import CompanyBillingFields, {
    EMPTY_COMPANY,
} from "@/components/Billing/CompanyBillingFields/CompanyBillingFields";
import BillingModeSwitch from "@/components/Billing/BillingModeSwitch/BillingModeSwitch";
import { validateCompanyBilling } from "@/lib/companyBilling";
import nProgress from "nprogress";
import { toast } from "react-toastify";
import { loadStripe } from "@stripe/stripe-js";
import { SpinnerDotted } from "spinners-react";
import Logo from "@/components/ui/Logo/Logo";
import { sha256 } from "js-sha256";
import moment from "moment-timezone";
import Footer from "@/components/ui/Footer/Footer";
import WOL from "@/components/ui/Testimonials/WOL/WOL";
import { ShineBorder } from "@/components/magicui/shine-border";
import { sendMetaEvent } from "@/lib/tracking/metaEvent";

// Personnalisation selon l'objectif déclaré à l'onboarding (ou le sélecteur
// pour qui n'a pas encore répondu) : hero, ordre des arguments, parcours
import {
    buildPricingPersonalization,
    PRICING_INTENT_COOKIE,
    PRICING_INTENT_COOKIE_MAX_AGE,
} from "@/lib/pricingPersonalization";
import { useCommerceSettings } from "@/contexts/CommerceSettingsContext";
import { trialReminderDay as trialReminderDayFor } from "@/lib/trialReminder";
import PricingIntentPicker from "./PricingIntentPicker";
import PricingPersonalPath from "./PricingPersonalPath";
import useCommunityCount from "@/hooks/useCommunityCount";

const TableRow = ({ label, values }) => (
    <tr className="border-b border-gray-100 dark:border-borderDark">
        <td className="p-4">{label}</td>
        {values.map((v, i) => (
            <td key={i} className="p-4 text-center">
                {v === "✓" ? (
                    <span className="text-green-600 dark:text-green-400">
                        ✓
                    </span>
                ) : v === "-" ? (
                    <span className="text-gray-400">–</span>
                ) : (
                    <span className="text-gray-400">{v}</span>
                )}
            </td>
        ))}
    </tr>
);

// Solar Icons (CC BY 4.0) — used in Founder checkout flow
const SolarCrown = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        <path d="m20.092 14.326l.193-1.894c.103-1.011.17-1.678.117-2.099h.02c.871 0 1.578-.746 1.578-1.666S21.293 7 20.421 7s-1.579.746-1.579 1.667c0 .416.145.797.384 1.089c-.343.223-.792.695-1.468 1.405c-.52.547-.78.82-1.07.863a.84.84 0 0 1-.473-.07c-.268-.124-.447-.462-.804-1.139L13.527 7.25c-.22-.417-.405-.766-.572-1.047c.683-.368 1.15-1.117 1.15-1.98C14.105 2.994 13.163 2 12 2s-2.105.995-2.105 2.222c0 .864.467 1.613 1.15 1.98c-.167.282-.351.631-.572 1.048L8.59 10.816c-.358.676-.537 1.014-.805 1.139a.84.84 0 0 1-.473.07c-.29-.043-.55-.317-1.07-.864c-.676-.71-1.125-1.182-1.468-1.405c.24-.292.384-.673.384-1.09C5.158 7.747 4.45 7 3.578 7C2.708 7 2 7.746 2 8.667c0 .92.707 1.666 1.579 1.666h.019c-.054.42.014 1.088.117 2.099l.193 1.894c.107 1.051.196 2.051.306 2.952h15.572c.11-.9.199-1.901.306-2.952M10.855 22h2.29c2.985 0 4.478 0 5.474-.94c.434-.412.71-1.152.908-2.116H4.473c.198.964.473 1.704.908 2.115C6.377 22 7.87 22 10.855 22" />
    </svg>
);

const SolarBolt = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        <path d="m5.67 9.914l3.062-4.143c1.979-2.678 2.969-4.017 3.892-3.734s.923 1.925.923 5.21v.31c0 1.185 0 1.777.379 2.148l.02.02c.387.363 1.003.363 2.236.363c2.22 0 3.329 0 3.704.673l.018.034c.354.683-.289 1.553-1.574 3.29l-3.062 4.144c-1.98 2.678-2.969 4.017-3.892 3.734s-.923-1.925-.923-5.21v-.31c0-1.185 0-1.777-.379-2.148l-.02-.02c-.387-.363-1.003-.363-2.236-.363c-2.22 0-3.329 0-3.703-.673l-.019-.034c-.354-.683.289-1.552 1.574-3.29" />
    </svg>
);

const SolarCalendar = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        <path d="M7.75 2.5a.75.75 0 0 0-1.5 0v1.58c-1.44.115-2.384.397-3.078 1.092c-.695.694-.977 1.639-1.093 3.078h19.842c-.116-1.44-.398-2.384-1.093-3.078c-.694-.695-1.639-.977-3.078-1.093V2.5a.75.75 0 0 0-1.5 0v1.513C15.585 4 14.839 4 14 4h-4c-.839 0-1.585 0-2.25.013z" />
        <path fillRule="evenodd" clipRule="evenodd" d="M2 12c0-.839 0-1.585.013-2.25h19.974C22 10.415 22 11.161 22 12v2c0 3.771 0 5.657-1.172 6.828S17.771 22 14 22h-4c-3.771 0-5.657 0-6.828-1.172S2 17.771 2 14zm15 2a1 1 0 1 0 0-2a1 1 0 0 0 0 2m0 4a1 1 0 1 0 0-2a1 1 0 0 0 0 2m-4-5a1 1 0 1 1-2 0a1 1 0 0 1 2 0m0 4a1 1 0 1 1-2 0a1 1 0 0 1 2 0m-6-3a1 1 0 1 0 0-2a1 1 0 0 0 0 2m0 4a1 1 0 1 0 0-2a1 1 0 0 0 0 2" />
    </svg>
);

const SolarMedalRibbonsStar = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        <path fillRule="evenodd" clipRule="evenodd" d="M12 16a7 7 0 1 0 0-14a7 7 0 0 0 0 14m0-10c-.284 0-.474.34-.854 1.023l-.098.176c-.108.194-.162.29-.246.354c-.085.064-.19.088-.4.135l-.19.044c-.738.167-1.107.25-1.195.532s.164.577.667 1.165l.13.152c.143.167.215.25.247.354s.021.215 0 .438l-.02.203c-.076.785-.114 1.178.115 1.352c.23.174.576.015 1.267-.303l.178-.082c.197-.09.295-.135.399-.135s.202.045.399.135l.178.082c.691.319 1.037.477 1.267.303s.191-.567.115-1.352l-.02-.203c-.021-.223-.032-.334 0-.438s.104-.187.247-.354l.13-.152c.503-.588.755-.882.667-1.165c-.088-.282-.457-.365-1.195-.532l-.19-.044c-.21-.047-.315-.07-.4-.135c-.084-.064-.138-.16-.246-.354l-.098-.176C12.474 6.34 12.284 6 12 6" />
        <path d="M4.495 12.995L2.992 14.55c-.54.56-.81.839-.904 1.076c-.213.54-.03 1.138.433 1.422c.204.124.57.163 1.305.24c.414.044.622.066.795.133c.389.149.69.462.835.864c.064.18.085.394.127.823c.075.76.113 1.14.233 1.351c.274.48.853.669 1.374.448c.228-.096.498-.376 1.039-.935l2.482-2.57a8.5 8.5 0 0 1-6.216-4.408m8.795 4.409l2.482 2.57c.54.56.81.839 1.038.936c.521.22 1.1.031 1.374-.449c.12-.21.157-.59.232-1.35c.043-.43.064-.644.128-.824c.144-.402.446-.715.835-.864c.173-.067.38-.088.795-.132c.734-.078 1.101-.117 1.305-.241c.463-.284.646-.883.433-1.422c-.094-.237-.364-.517-.904-1.076l-1.503-1.556a8.5 8.5 0 0 1-6.216 4.408" />
    </svg>
);

// Champs du tunnel, style "Stripe" : fond blanc, bord 1px net + ombre légère,
// un seul accent orange (le focus). Le label fonce quand son champ a le focus
// (wrapper .group). text-[16px] sur mobile : en dessous, iOS zoome au focus.
const CHECKOUT_LABEL_CLASS =
    "block text-[13px] font-medium text-stone-700 group-focus-within:text-stone-900 transition-colors mb-1";
const CHECKOUT_INPUT_CLASS =
    "w-full rounded-[10px] border border-stone-300 bg-white px-3 py-2.5 text-[16px] sm:text-[14px] leading-5 text-stone-900 shadow-sm placeholder:text-stone-400 hover:border-stone-400 focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-hidden transition-[border-color,box-shadow] duration-150";
const CHECKOUT_SELECT_CLASS =
    CHECKOUT_INPUT_CLASS + " appearance-none cursor-pointer pr-9 truncate";

const SolarShieldCheck = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        <path fillRule="evenodd" clipRule="evenodd" d="M3.378 5.082C3 5.62 3 7.22 3 10.417v1.574c0 5.638 4.239 8.375 6.899 9.536c.721.315 1.082.473 2.101.473c1.02 0 1.38-.158 2.101-.473C16.761 20.365 21 17.63 21 11.991v-1.574c0-3.198 0-4.797-.378-5.335c-.377-.537-1.88-1.052-4.887-2.081l-.573-.196C13.595 2.268 12.812 2 12 2s-1.595.268-3.162.805L8.265 3c-3.007 1.03-4.51 1.545-4.887 2.082M15.06 10.5a.75.75 0 0 0-1.12-.999l-3.011 3.374l-.87-.974a.75.75 0 0 0-1.118 1l1.428 1.6a.75.75 0 0 0 1.119 0z" />
    </svg>
);

function PricingComponentInner({
    session,
    initialGeo,
    initialCheckoutType,
    personalization,
    commerceSettings,
    ...props
}) {
    // Variables
    const { executeRecaptcha } = useGoogleReCaptcha();
    // Générations de checkout : incrémentée à chaque ouverture/fermeture du
    // modal pour ignorer (et annuler) une création d'abonnement arrivée trop tard.
    const checkoutGenerationRef = useRef(0);
    // Deep link /pricing?checkout=<type> : ouverture unique du tunnel au montage
    const autoCheckoutDoneRef = useRef(false);
    const recaptchaV2Ref = useRef(null);
    const locale = useLocale();
    // Le compte réel remplace la valeur figée dès qu'il arrive.
    const { nombre: nombreCommunaute, exact: compteExact } = useCommunityCount();
    const t = useTranslations();

    // Session côté client : le prop `session` est figé au rendu serveur. Après
    // un signIn() dans le modal (visiteur qui crée son compte), seul useSession
    // reflète la nouvelle session ; sans lui, l'étape paiement plantait sur
    // session.user pour tout visiteur non connecté.
    const { data: clientSession, update: updateSession } = useSession();
    const activeSession = clientSession ?? session;

    // Personnalisation résolue côté serveur (aucun scintillement au premier
    // rendu), puis recalculée localement quand le sélecteur change l'intention.
    const [personalizationState, setPersonalizationState] = useState(
        () => personalization || buildPricingPersonalization({ locale }),
    );
    const {
        intent,
        source: intentSource,
        goalLabelKey,
        heroTitleKey,
        heroSubtitleKey,
        features: premiumFeatures,
        path: personalPath,
        otherCoursesCount,
    } = personalizationState;

    // Figé au montage : une fois la question posée, le sélecteur reste visible
    // pour que la personne puisse changer d'avis après avoir vu la page changer.
    const [showIntentPicker] = useState(
        () => personalizationState.showSelector,
    );

    // Jointes à chaque événement du tunnel : comparer les conversions par
    // intention ne demande alors aucun test A/B. Passées par une ref parce que
    // les callbacks du tunnel sont mémoïsés : sans elle, un changement
    // d'intention après leur création enverrait l'ancienne valeur.
    const intentPropertiesRef = useRef({
        pricing_intent: intent,
        pricing_intent_source: intentSource,
    });
    useEffect(() => {
        intentPropertiesRef.current = {
            pricing_intent: intent,
            pricing_intent_source: intentSource,
        };
    }, [intent, intentSource]);

    // Choix du sélecteur : la page se recompose sans rechargement, et le cookie
    // permet au rendu serveur de retrouver l'intention à la prochaine visite.
    const handleIntentChange = (nextIntent) => {
        document.cookie = `${PRICING_INTENT_COOKIE}=${nextIntent}; path=/; max-age=${PRICING_INTENT_COOKIE_MAX_AGE}; SameSite=Lax`;
        setPersonalizationState(
            buildPricingPersonalization({
                goal: activeSession?.user?.goal || null,
                category: activeSession?.user?.category || null,
                cookieIntent: nextIntent,
                locale,
            }),
        );
        posthog.capture("pricing_intent_selected", { intent: nextIntent });
    };

    // Réglages commerciaux pilotés depuis /admin/promotions. La page les
    // reçoit du serveur (props) : la remise est bonne dès le premier octet,
    // sans clignotement. Le contexte ne sert que de filet si un jour cette
    // page était rendue sans les passer.
    const { settings: contextCommerceSettings } = useCommerceSettings();
    const effectiveCommerceSettings = commerceSettings || contextCommerceSettings;
    const activePromotion = effectiveCommerceSettings.promotion;
    const freeOfferEnabled = effectiveCommerceSettings.freeOffer.enabled;
    const pricingTrial = effectiveCommerceSettings.trial.pricing;
    // Jour du rappel par e-mail dans la timeline de l'essai, 0 quand l'essai
    // est trop court pour en avoir un (lib/trialReminder.js)
    const trialReminderDay = trialReminderDayFor(pricingTrial.days);

    // Comparatif : la première colonne est Starter, c'est-à-dire l'offre
    // gratuite. Quand elle est coupée, la colonne entière disparaît du
    // tableau, en-tête comprise.
    const comparisonCols = (values) =>
        freeOfferEnabled ? values : values.slice(1);

    // Campagne influenceur (cookie blvmy_ref / attribution en base).
    // RÈGLE PRODUIT : quand elle est active, la promo flash Believemy est
    // entièrement masquée, même si elle est plus avantageuse.
    const { campaign: rawInfluencerCampaign } = useInfluencerCampaign(
        !!(activeSession && activeSession.user),
    );
    // Garde d'expiration : si la campagne expire pendant la navigation, on
    // cesse de l'afficher au prochain rendu (le serveur re-vérifie de toute
    // façon isCampaignActive au moment de créer le paiement).
    const influencerCampaign =
        rawInfluencerCampaign &&
        (!rawInfluencerCampaign.endsAt ||
            new Date(rawInfluencerCampaign.endsAt) > new Date())
            ? rawInfluencerCampaign
            : null;

    // Helper function pour formater les prix
    const formatPrice = (price) => {
        // Tolere une virgule decimale en entree (ex: "4,99") et formate selon la
        // locale : virgule en FR, point en EN. Evite parseFloat("4,99") === 4.
        const n = parseFloat(String(price).replace(",", "."));
        if (Number.isNaN(n)) return price;
        const formatted = n.toFixed(2);
        const trimmed = formatted.endsWith(".00")
            ? formatted.slice(0, -3)
            : formatted;
        return locale && locale.startsWith("en")
            ? trimmed
            : trimmed.replace(".", ",");
    };

    // Promotion settings - logique similaire à UserLayout
    //
    // La fenêtre de la promotion (début, fin, interrupteur) est calculée par
    // le serveur et arrive dans commerceSettings : ce composant ne compare
    // plus de dates, il applique. Ne restent ici que les règles qui dépendent
    // de la PERSONNE : abonné, équipe, campagne influenceur.
    const PROMO_CONFIG = {
        isActive: (() => {
            if (!activePromotion) {
                return false;
            }

            if (activeSession?.user?.pro || activeSession?.user?.currentTeam) {
                return false;
            }

            // Campagne influenceur active : la promo flash disparaît
            // entièrement (bandeau, badges, prix barrés, compte à rebours),
            // seule l'offre du créateur est montrée. Décision produit.
            if (influencerCampaign) {
                return false;
            }

            return true;
        })(),
        discount: activePromotion ? activePromotion.discountPercent : null,
        nameFr: activePromotion ? activePromotion.nameFr : null,
        nameEn: activePromotion ? activePromotion.nameEn : null,

        // Date de fin de la promotion (ISO), pour le compte à rebours
        endDate: activePromotion ? activePromotion.endsAt : null,

        // Calculs centralisés des prix et économies
        prices: {
            // Prix de base
            monthlyBase: parseFloat(
                process.env.BELIEVEMY_PREMIUM_MONTHLY_PRICE,
            ), // Prix mensuel de l'abonnement mensuel
            annuallyBase: parseFloat(
                process.env.BELIEVEMY_PREMIUM_ANNUALLY_PRICE,
            ), // Prix mensuel de l'abonnement annuel (tarif mensualisé)

            // Prix avec promotion appliquée
            getMonthlyPrice() {
                return this.monthlyBase; // Le prix mensuel n'est pas affecté par les promotions
            },

            // Prix mensuel équivalent de l'abonnement annuel (avec promo si active)
            getAnnuallyPrice() {
                if (PROMO_CONFIG.isActive && PROMO_CONFIG.discount) {
                    // Arrondir le total annuel, puis diviser par 12
                    const annualTotal = this.annuallyBase * 12 * (1 - PROMO_CONFIG.discount / 100);
                    return Math.floor(annualTotal) / 12;
                }
                return this.annuallyBase;
            },

            // Calcul des économies annuelles (vs prix mensuel x 12)
            getAnnualSavings() {
                const monthlyTotal = this.getMonthlyPrice() * 12;
                const annualTotal = this.getAnnualTotalPrice();
                return monthlyTotal - annualTotal;
            },

            // Prix annuel total avec promotion (arrondi à l'euro inférieur)
            getAnnualTotalPrice() {
                if (PROMO_CONFIG.isActive && PROMO_CONFIG.discount) {
                    const rawPrice = this.annuallyBase * 12 * (1 - PROMO_CONFIG.discount / 100);
                    return Math.floor(rawPrice);
                }
                return this.annuallyBase * 12;
            },

            // Prix annuel total SANS promotion (pour afficher le prix barré)
            getAnnualBaseTotalPrice() {
                return this.annuallyBase * 12;
            },

            // Économies grâce à la promo (vs prix annuel sans promo)
            getPromoSavings() {
                if (PROMO_CONFIG.isActive && PROMO_CONFIG.discount) {
                    return this.getAnnualBaseTotalPrice() - this.getAnnualTotalPrice();
                }
                return 0;
            },

            // Montant de la réduction appliquée (mensuel)
            getDiscountAmount() {
                if (PROMO_CONFIG.isActive && PROMO_CONFIG.discount) {
                    return this.annuallyBase - this.getAnnuallyPrice();
                }
                return 0;
            },
        },
    };

    // Free call bonus settings
    const FREE_CALL_CONFIG = {
        isActive: (() => {
            const freeCallEnabled =
                process.env.SHOULD_OFFER_FREE_CALL_WITH_LOUIS == "true";

            if (!freeCallEnabled) {
                return false;
            }

            const endDate = process.env.LIMIT_DATE_FOR_FREE_CALL;
            if (endDate) {
                try {
                    const currentDate = moment().tz("Europe/Paris");
                    const limitDate = moment.tz(endDate, "Europe/Paris");

                    if (!limitDate.isValid()) {
                        return false;
                    }

                    if (currentDate.isAfter(limitDate)) {
                        return false;
                    }
                } catch (error) {
                    return false;
                }
            }

            return true;
        })(),
        endDate: process.env.LIMIT_DATE_FOR_FREE_CALL,
        value: 130,
        getFormattedEndDate(locale) {
            if (!this.endDate) return "";
            const date = moment.tz(this.endDate, "Europe/Paris");
            return locale === "fr" ? date.format("DD/MM") : date.format("MMM D");
        },
    };

    // States
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [price, setPrice] = useState(49.99);
    const [type, setType] = useState("monthly");
    const [billingPeriod, setBillingPeriod] = useState("annually"); // Annuel par défaut (mis en avant)
    const [clientSecret, setClientSecret] = useState(null);
    const [pendingSubscriptionId, setPendingSubscriptionId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [preparingPayment, setPreparingPayment] = useState(false); // Connecté avec adresse connue : on saute le formulaire
    const [formError, setFormError] = useState(null);
    const [addressStreet, setAddressStreet] = useState("");
    const [addressOption, setAddressOption] = useState("");
    const [addressPostal, setAddressPostal] = useState("");
    const [addressCity, setAddressCity] = useState("");
    const [addressCountry, setAddressCountry] = useState(
        initialGeo?.country || "FR",
    );
    // « Je paie pour une entreprise » : la facture part au nom de
    // l'entreprise (lib/stripeCompanyBilling.js). Le pays et le code postal
    // du formulaire deviennent alors ceux de l'entreprise. savedCompany est
    // l'entreprise déjà enregistrée sur la fiche Stripe du membre (null s'il
    // n'y en a pas) ; tant qu'elle n'est pas lue (undefined), le tunnel ne
    // demande jamais au serveur d'en retirer une.
    const [companyEnabled, setCompanyEnabled] = useState(false);
    const [company, setCompany] = useState(EMPTY_COMPANY);
    const [savedCompany, setSavedCompany] = useState(undefined);
    const personalLocationRef = useRef(null);
    // Pays triés dans la langue du visiteur, champ code postal adapté au pays
    // choisi (masqué là où il n'existe pas, obligatoire aux US et au Canada)
    const countryOptions = useCountryOptions();
    const postalCodeField = getPostalCodeField(addressCountry);
    // L'adresse telle qu'elle part au serveur : code postal remis en forme,
    // et vidé si le pays choisi n'en utilise pas (une saisie faite avant de
    // changer de pays ne doit pas partir). Le state garde la frappe brute,
    // pour la retrouver si l'on revient au pays précédent.
    const billingAddress = useMemo(
        () => ({
            addressCity,
            addressCountry,
            addressOption,
            addressPostal: normalizePostalCode(addressCountry, addressPostal),
            addressStreet,
        }),
        [addressCity, addressCountry, addressOption, addressPostal, addressStreet],
    );

    // Entreprise déjà enregistrée (Mon compte ou paiement précédent) : le
    // formulaire la retrouve, interrupteur activé.
    const activeUserId = activeSession?.user?.userId;
    useEffect(() => {
        if (!activeUserId) return undefined;
        let active = true;
        fetch("/api/user/billing-company")
            .then((r) => (r.ok ? r.json() : Promise.reject(new Error(r.status))))
            .then((data) => {
                if (!active) return;
                setSavedCompany(data.company || null);
                if (data.company) {
                    setCompany({ ...EMPTY_COMPANY, ...data.company });
                    setCompanyEnabled(true);
                }
            })
            .catch(() => {
                // Lecture impossible : savedCompany reste inconnu, et rien
                // ne sera retiré côté serveur.
            });
        return () => {
            active = false;
        };
    }, [activeUserId]);

    const [emailAddress, setEmailAddress] = useState("");
    const emailInputRef = useRef(null);
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [conversionSend, setConversionSend] = useState(false);
    const [openFaq, setOpenFaq] = useState(null);
    const [showRecaptchaV2, setShowRecaptchaV2] = useState(false);
    const [recaptchaV2Token, setRecaptchaV2Token] = useState(null);
    const [pendingCreateUser, setPendingCreateUser] = useState(null);
    const [promoCountdown, setPromoCountdown] = useState(null);
    // Géo résolue côté serveur (page.jsx) et passée en prop : la promesse
    // d'essai est visible dès le premier rendu. Le fetch client ci-dessous ne
    // sert que de confirmation (fallback ip-api en absence de headers géo).
    const [isTrialAllowedByGeo, setIsTrialAllowedByGeo] = useState(
        initialGeo?.isTrialAllowed ?? false,
    );
    const [geoCountry, setGeoCountry] = useState(initialGeo?.country ?? null);
    const [isAfricaPricing, setIsAfricaPricing] = useState(
        initialGeo?.isAfricaPricing ?? false,
    );

    // Géolocalisation pour la période d'essai (confirmation client)
    useEffect(() => {
        const checkGeoForTrial = async () => {
            try {
                const response = await fetch("/api/geo/country");
                const data = await response.json();

                if (data.success) {
                    setIsTrialAllowedByGeo(data.isTrialAllowed);
                    setGeoCountry(data.country);
                    setIsAfricaPricing(data.isAfricaPricing || false);
                }
                // En cas d'échec, on conserve la valeur résolue côté serveur
            } catch (error) {
                console.warn("Geo check failed:", error);
            }
        };

        checkGeoForTrial();
    }, []);

    // Quand la tarification Afrique est détectée, basculer le toggle sur le tarif engagé
    useEffect(() => {
        if (isAfricaPricing) {
            setBillingPeriod("africa-committed");
        } else {
            // Si la géo client corrige la géo serveur, revenir au tarif standard
            setBillingPeriod((bp) =>
                bp.startsWith("africa") ? "annually" : bp,
            );
        }
    }, [isAfricaPricing]);

    // Mapping billingPeriod → clé STRIPE_PRICES pour déterminer l'affichage dynamique
    const BILLING_TO_STRIPE_KEY = {
        monthly: "monthly",
        annually: "annually",
        "africa-monthly": "africaMonthly",
        "africa-committed": "africaCommitted",
    };

    // Détermine si un billingPeriod utilise la facturation mensuelle (avec engagement)
    // NONE (null) = payé d'avance → afficher /an
    // Non-null (ex: TWELVE_MONTHS) = paie tous les mois → afficher /mois
    const getIsMonthlyBilling = (bp) => {
        const stripeKey = BILLING_TO_STRIPE_KEY[bp];
        if (!stripeKey) return true;
        const engagement = STRIPE_PRICES.premium[stripeKey]?.current?.engagement;
        return engagement !== null;
    };

    const annuallyIsMonthlyBilling = getIsMonthlyBilling("annually");
    const africaCommittedIsMonthlyBilling = getIsMonthlyBilling("africa-committed");

    // Pour le checkout : faut-il afficher le total annuel (payé d'avance) ou le prix mensuel ?
    // true = payé d'avance en une fois → afficher price * 12
    // false = facturation mensuelle → afficher price tel quel
    const isCheckoutUpfrontAnnual =
        (type === "annually" && !annuallyIsMonthlyBilling) ||
        (type === "africa-committed" && !africaCommittedIsMonthlyBilling);

    // Countdown timer for promotion
    useEffect(() => {
        if (!PROMO_CONFIG.isActive || !PROMO_CONFIG.endDate) return;

        const calculateTimeLeft = () => {
            const now = moment().tz("Europe/Paris");
            const end = moment.tz(PROMO_CONFIG.endDate, "Europe/Paris");
            const diff = end.diff(now);

            if (diff <= 0) {
                setPromoCountdown(null);
                return;
            }

            const duration = moment.duration(diff);
            const days = Math.floor(duration.asDays());
            const hours = duration.hours();
            const minutes = duration.minutes();

            // Pas de décompte à la seconde : l'urgence reste lisible sans
            // basculer dans le registre infopreneur.
            const dayUnit = locale && locale.startsWith("en") ? "d" : "j";
            if (days > 0) {
                setPromoCountdown(`${days}${dayUnit} ${hours}h`);
            } else {
                setPromoCountdown(`${hours}h ${minutes}min`);
            }
        };

        calculateTimeLeft();
        const timer = setInterval(calculateTimeLeft, 30000);

        return () => clearInterval(timer);
    }, [PROMO_CONFIG.isActive, PROMO_CONFIG.endDate]);

    // Handle reCAPTCHA v2 callback
    const handleRecaptchaV2Change = (token) => {
        setRecaptchaV2Token(token);
    };

    // Trusted logos
    const trustedLogos = [
        { src: "/static/companies/google.webp", alt: "Google" },
        { src: "/static/companies/capgemini.webp", alt: "Capgemini" },
        { src: "/static/companies/deloitte.webp", alt: "Deloitte" },
        { src: "/static/companies/amazon.webp", alt: "Amazon" },
        { src: "/static/companies/ibm.webp", alt: "IBM" },
        { src: "/static/companies/accenture.webp", alt: "Accenture" },
    ];

    // Total annuel de l'offre Plus (env "39,99") pour l'équivalent mensuel
    const plusAnnualTotal = parseFloat(
        String(process.env.BELIEVEMY_ESSENTIAL_ANNUALLY_TOTAL).replace(
            ",",
            ".",
        ),
    );

    // Campagne influenceur : pourcentage et application aux prix affichés.
    // La réduction réelle est appliquée côté serveur (coupon Stripe percent_off,
    // fenêtre 11 mois = première année) via get-referral-discount.
    const campaignPercent = influencerCampaign ? influencerCampaign.percent : 0;
    // Formules couvertes par la campagne (vide = toutes) : plus / pro / founder
    const campaignPlans =
        influencerCampaign && influencerCampaign.plans
            ? influencerCampaign.plans
            : [];
    const planHasCampaign = (plan) =>
        campaignPercent > 0 &&
        (campaignPlans.length === 0 || campaignPlans.includes(plan));
    const applyCampaign = (value) => {
        const n =
            typeof value === "number"
                ? value
                : parseFloat(String(value).replace(",", "."));
        if (Number.isNaN(n)) return value;
        if (!campaignPercent) return n;
        return Math.round(n * (100 - campaignPercent)) / 100;
    };
    // Applique la remise seulement si la formule est couverte par la campagne
    const applyCampaignTo = (plan, value) => {
        const n =
            typeof value === "number"
                ? value
                : parseFloat(String(value).replace(",", "."));
        if (Number.isNaN(n)) return value;
        return planHasCampaign(plan) ? applyCampaign(n) : n;
    };
    const campaignEndsAtLabel = influencerCampaign?.endsAt
        ? moment(influencerCampaign.endsAt).format(
              locale && locale.startsWith("en") ? "MM/DD/YYYY" : "DD/MM/YYYY",
          )
        : "";

    // L'essai gratuit Premium est-il envisageable pour ce visiteur ?
    // (essai ouvert depuis /admin/promotions + géo autorisée + pas de verrou
    // d'essai déjà consommé). Les trois conditions sont revérifiées par le
    // serveur au moment de créer l'abonnement.
    const premiumTrialAvailable =
        pricingTrial.enabled &&
        isTrialAllowedByGeo &&
        !activeSession?.user?.isTrialLocked;

    // FAQ items — ordre par poids d'objection
    // Prix dynamiques pour les FAQ tarifaires (alignés sur l'affichage de la card)
    const faqAnnualMonthlyPrice = formatPrice(
        PROMO_CONFIG.prices.getAnnuallyPrice(),
    );
    const faqMonthlyPrice = formatPrice(PROMO_CONFIG.prices.getMonthlyPrice());
    const faqAnnualSavings = formatPrice(
        PROMO_CONFIG.prices.getAnnualSavings(),
    );

    const faqs = [
        // Bloc 1 — Suis-je capable ?
        {
            question: t("pricing_faq_beginner"),
            answer: t("pricing_faq_beginner_description"),
        },
        {
            question: t("pricing_faq_no_code"),
            answer: t("pricing_faq_no_code_description"),
        },
        {
            question: t("pricing_faq_already_dev"),
            answer: t("pricing_faq_already_dev_description"),
        },
        // Bloc 2 — Vais-je en tirer un résultat ?
        {
            question: t("pricing_faq_time_to_launch"),
            answer: t("pricing_faq_time_to_launch_description"),
        },
        {
            question: t("pricing_faq_certifications"),
            answer: t("pricing_faq_certifications_description"),
        },
        // Bloc 3 — Pourquoi payer ?
        {
            question: t("pricing_faq_free_content"),
            answer: t("pricing_faq_free_content_description"),
        },
        {
            question: t("pricing_faq_up_to_date"),
            answer: t("pricing_faq_up_to_date_description"),
        },
        // Bloc 4 — Le prix est-il justifié ?
        {
            question: t("pricing_faq_price", { price: faqAnnualMonthlyPrice }),
            answer: t("pricing_faq_price_description"),
        },
        // L'entrée essai gratuit ne s'affiche que si le tunnel l'accordera
        // vraiment (géo + verrou) : sinon on met en avant la garantie 14 jours.
        // CGV : la garantie de 14 jours sur Believemy Pro est l'article 15 des
        // CGV (app/[locale]/(app)/terms-of-sale). Toute modification de son
        // périmètre, de sa durée ou de ses conditions doit y être reportée.
        premiumTrialAvailable
            ? {
                  question: t("pricing_faq_debit"),
                  answer: t("pricing_faq_debit_description", {
                      freeDays: pricingTrial.days,
                  }),
              }
            : {
                  question: t("pricing_faq_guarantee"),
                  answer: t("pricing_faq_guarantee_description"),
              },
        {
            question: t("pricing_faq_diff"),
            answer: t("pricing_faq_diff_description", {
                annualMonthlyPrice: faqAnnualMonthlyPrice,
                monthlyPrice: faqMonthlyPrice,
                savings: faqAnnualSavings,
            }),
        },
        {
            question: t("pricing_faq_cancel"),
            answer: t("pricing_faq_cancel_description"),
        },
        // Bloc 5 — Timing
        {
            question: t("pricing_faq_part_time"),
            answer: t("pricing_faq_part_time_description"),
        },
    ];

    useEffect(() => {
        if (
            activeSession &&
            activeSession.user &&
            (activeSession.user.pro ||
                activeSession.user.essential ||
                activeSession.user.ultimate)
        ) {
            window.location.href = "/";
        }
    });

    // Methods
    /**
     * Annule chez Stripe une tentative de paiement laissée en plan.
     *
     * Le tunnel crée l'abonnement AVANT que la carte ne soit saisie : c'est ce
     * qui permet d'afficher le formulaire de paiement. Toute tentative
     * abandonnée laisse donc une ligne vivante côté Stripe, qu'il faut fermer
     * nous-mêmes — sinon elle traîne jusqu'au cron horaire.
     *
     * Sans attente : rien de ce qui suit ne dépend de la réponse, et faire
     * patienter quelqu'un qui vient de cliquer sur une autre offre serait
     * absurde. Une erreur ici n'a aucune conséquence visible, le cron
     * cleanup-incomplete-subscriptions reste le filet.
     */
    const cancelPendingSubscription = useCallback(
        (subscriptionId) => {
            if (!subscriptionId) return;
            fetch("/api/sales/cancel-incomplete-subscription", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ subscriptionId, locale }),
            }).catch((error) => {
                console.error(
                    "Annulation de la tentative précédente impossible:",
                    error,
                );
            });
        },
        [locale],
    );

    const onStartPremium = (offerType) => {
        // Offre Afrique demandée hors d'Afrique (lien partagé, URL modifiée) :
        // on ouvre l'offre standard équivalente, le serveur refuserait
        // l'autre (create-payment-intent juge sur l'adresse IP, comme ici)
        const requestedType =
            isAfricaCheckoutType(offerType) && !isAfricaPricing
                ? AFRICA_CHECKOUT_FALLBACK[offerType]
                : offerType;
        const TYPE_PRICES = {
            "africa-committed":
                process.env.BELIEVEMY_PREMIUM_AFRICA_COMMITTED_PRICE,
            "africa-monthly":
                process.env.BELIEVEMY_PREMIUM_AFRICA_MONTHLY_PRICE,
            annually: PROMO_CONFIG.prices.getAnnuallyPrice(),
            "6-months": process.env.BELIEVEMY_PREMIUM_SIX_MONTHS_PRICE,
            ultra: process.env.BELIEVEMY_ULTRA_MONTHLY_PRICE,
            essential: process.env.BELIEVEMY_ESSENTIAL_MONTHLY_PRICE,
            "essential-annually":
                process.env.BELIEVEMY_ESSENTIAL_ANNUALLY_TOTAL,
            monthly: PROMO_CONFIG.prices.getMonthlyPrice(),
            // Offre de retour : la page ne transmet ce type qu'à un ancien
            // abonné dont l'offre court encore (page.jsx), le serveur revérifie
            [WINBACK_CHECKOUT_TYPE]: getWinbackPrice(
                process.env.BELIEVEMY_PREMIUM_MONTHLY_PRICE,
            ),
        };
        const resolvedType = TYPE_PRICES[requestedType]
            ? requestedType
            : "monthly";
        setType(resolvedType);
        setPrice(TYPE_PRICES[resolvedType]);
        setFormError(null);
        // Nouvelle tentative de checkout : purger tout clientSecret d'une
        // tentative précédente (sinon le modal rouvrirait sur l'ancien
        // paiement) et invalider les réponses encore en vol.
        //
        // Et surtout : ANNULER l'abonnement de la tentative précédente. Le
        // state était remis à zéro sans le faire, si bien que comparer deux
        // offres formulaire ouvert laissait une ligne vivante chez Stripe
        // jusqu'au passage du cron, une heure plus tard. Même geste qu'à la
        // fermeture du modal — la seule différence, c'est qu'on ne l'attend
        // pas, la nouvelle offre doit s'ouvrir tout de suite.
        if (pendingSubscriptionId && clientSecret) {
            cancelPendingSubscription(pendingSubscriptionId);
        }

        checkoutGenerationRef.current += 1;
        setClientSecret(null);
        setPendingSubscriptionId(null);

        posthog.capture("pricing_modal_opened", {
            type: resolvedType,
            ...intentPropertiesRef.current,
        });

        const user = activeSession?.user;
        if (user && user.email) {
            const country = user.addressCountry || geoCountry || "FR";
            const address = {
                addressCity: user.addressCity || "",
                addressCountry: country,
                addressOption: user.addressOption || "",
                addressPostal: normalizePostalCode(country, user.addressPostal),
                addressStreet: user.addressStreet || "",
            };
            setAddressCity(address.addressCity);
            setAddressCountry(address.addressCountry);
            setAddressOption(address.addressOption);
            setAddressPostal(user.addressPostal || "");
            setAddressStreet(address.addressStreet);

            // Pays déjà en base, et code postal valable ou inutile pour ce
            // pays : aucun formulaire, on prépare directement le paiement
            // (tunnel à 1 étape pour les connectés). Un code américain ou
            // canadien mal formé rouvre le formulaire, erreur affichée.
            const storedPostalCodeError = user.addressCountry
                ? getPostalCodeError(country, address.addressPostal)
                : null;
            if (storedPostalCodeError && address.addressPostal) {
                setFormError(t(storedPostalCodeError));
            }
            if (user.addressCountry && !storedPostalCodeError) {
                setPreparingPayment(true);
                setModalIsOpen(true);
                continueToPaymentIntent({
                    type: resolvedType,
                    address,
                    skipCompany: true,
                })
                    .catch(() => {
                        // Erreur réseau : on retombe sur le formulaire d'adresse
                        toast.error(t("writer_unknown_error"));
                    })
                    .finally(() => setPreparingPayment(false));
                return;
            }
        } else {
            // Visiteur : pays pré-sélectionné via la géolocalisation
            setAddressCountry(geoCountry || "FR");
        }

        setModalIsOpen(true);
    };

    // Deep link paywall : /pricing?checkout=annually ouvre directement le
    // tunnel (les paywalls contextuels de l'app pointent ici : l'utilisateur
    // arrive à un clic du paiement, zéro navigation intermédiaire).
    useEffect(() => {
        if (!initialCheckoutType || autoCheckoutDoneRef.current) return;
        autoCheckoutDoneRef.current = true;
        onStartPremium(initialCheckoutType);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialCheckoutType]);

    // Focus automatique du premier champ à l'ouverture du formulaire,
    // desktop uniquement : sur mobile le clavier masquerait le récap de
    // l'offre (pointer: fine = souris/trackpad).
    useEffect(() => {
        if (!modalIsOpen || clientSecret || preparingPayment) return;
        if (activeSession?.user) return;
        if (!window.matchMedia("(pointer: fine)").matches) return;
        emailInputRef.current?.focus();
    }, [modalIsOpen, clientSecret, preparingPayment, activeSession]);

    // Submit create user with reCAPTCHA token
    const submitCreateUserWithRecaptcha = useCallback(
        async (recaptchaToken, recaptchaVersion) => {
            const response = await fetch("/api/sales/create-user", {
                method: "post",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    firstName,
                    lastName,
                    ...billingAddress,
                    email: emailAddress,
                    password,
                    locale,
                    recaptchaToken,
                    recaptchaVersion,
                }),
            });

            const fetchedData = await response.json();

            if (!response.ok) {
                // If reCAPTCHA v3 score is too low, show v2 challenge
                if (fetchedData.message === "RECAPTCHA_LOW_SCORE") {
                    setPendingCreateUser(true);
                    setShowRecaptchaV2(true);
                    setLoading(false);
                    nProgress.done();
                    toast.info(t("signup_recaptcha_verify"));
                    return false;
                }

                if (
                    fetchedData.message === "RECAPTCHA_FAILED" ||
                    fetchedData.message === "RECAPTCHA_REQUIRED"
                ) {
                    setLoading(false);
                    nProgress.done();
                    toast.error(t("signup_recaptcha_error"));
                    return false;
                }

                setLoading(false);
                nProgress.done();
                toast.error(fetchedData.message);
                return false;
            }

            // Log in user
            await signIn("credentials", {
                email: emailAddress,
                password: password,
                redirect: false,
            });
            // Rafraîchir la session client (le prop serveur reste figé)
            try {
                await updateSession?.();
            } catch (error) {
                // Non bloquant : la session cookie est posée, le paiement peut suivre
            }

            // GTM Event
            sendGTMEvent({
                event: "user_signup",
            });

            // PostHog Event
            posthog.capture("user_signup", intentPropertiesRef.current);

            // Pixel du navigateur et API Conversions sous le même identifiant
            // d'événement, hors du chemin critique vers le paiement : les deux
            // envois séparés comptaient l'inscription deux fois.
            sendMetaEvent("CompleteRegistration");

            return true;
        },
        [
            firstName,
            lastName,
            billingAddress,
            emailAddress,
            password,
            locale,
            t,
            updateSession,
        ],
    );


    // Continue to create payment intent after user creation/update.
    // `overrides` permet de passer type/adresse directement (les setState de
    // onStartPremium ne sont pas encore visibles dans la même frame).
    const continueToPaymentIntent = useCallback(
        async (overrides = {}) => {
            const effectiveType = overrides.type ?? type;
            const address = overrides.address ?? billingAddress;

            // Entreprise : l'objet la pose, null la retire (seulement s'il y en
            // avait une), absent ne change rien. Le paiement en un clic d'un
            // membre connecté (skipCompany) laisse sa fiche Stripe telle
            // quelle : une entreprise déjà enregistrée y reste, avec son pays.
            let companyForServer;
            if (!overrides.skipCompany) {
                if (companyEnabled) {
                    companyForServer = {
                        ...company,
                        country: address.addressCountry,
                        postalCode: address.addressPostal,
                    };
                } else if (savedCompany) {
                    companyForServer = null;
                }
            }

            const generation = checkoutGenerationRef.current;

            // Create a payment intent
            let response;
            let fetchedData;
            try {
                response = await fetch("/api/sales/create-payment-intent", {
                    method: "post",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        type: effectiveType,
                        locale,
                        ...address,
                        email:
                            activeSession && activeSession.user && activeSession.user.email
                                ? activeSession.user.email
                                : emailAddress,
                        firstName:
                            activeSession && activeSession.user && activeSession.user.firstName
                                ? activeSession.user.firstName
                                : firstName,
                        lastName:
                            activeSession && activeSession.user && activeSession.user.lastName
                                ? activeSession.user.lastName
                                : lastName,
                        reduction:
                            effectiveType == "annually"
                                ? PROMO_CONFIG.prices.getDiscountAmount()
                                : 0,
                        company: companyForServer,
                    }),
                });
                fetchedData = await response.json();
            } catch (error) {
                // Coupure réseau / réponse non JSON : ne jamais laisser le
                // tunnel bloqué en chargement.
                setLoading(false);
                nProgress.done();
                toast.error(t("writer_unknown_error"));
                return;
            }

            if (!response.ok) {
                setLoading(false);
                nProgress.done();
                // Code postal refusé par Stripe, ou entreprise à corriger :
                // l'erreur s'affiche sous le formulaire, qui reste (ou
                // revient) à l'écran pour corriger
                if (
                    fetchedData.field === "addressPostal" ||
                    fetchedData.field === "company"
                ) {
                    setFormError(fetchedData.message);
                } else {
                    toast.error(fetchedData.message);
                }
                return;
            }

            // Le modal a été fermé (ou rouvert sur une autre offre) pendant la
            // requête : on ignore cette réponse et on annule l'abonnement
            // incomplet créé côté Stripe pour ne pas laisser d'orphelin.
            if (generation !== checkoutGenerationRef.current) {
                try {
                    await fetch("/api/sales/cancel-incomplete-subscription", {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                            subscriptionId: fetchedData.subscriptionId,
                            locale,
                        }),
                    });
                } catch (error) {
                    console.error(
                        "Annulation d'un abonnement obsolète impossible:",
                        error,
                    );
                }
                setLoading(false);
                nProgress.done();
                return;
            }

            setClientSecret(fetchedData.clientSecret);
            setPendingSubscriptionId(fetchedData.subscriptionId);

            posthog.capture("pricing_payment_shown", {
                type: effectiveType,
                trial: String(fetchedData.clientSecret || "").startsWith(
                    "seti_",
                ),
                ...intentPropertiesRef.current,
            });

            const event = new Event("visibilitychange");
            document.dispatchEvent(event);
            setLoading(false);
            nProgress.done();
        },
        [
            type,
            billingAddress,
            activeSession,
            emailAddress,
            firstName,
            lastName,
            locale,
            PROMO_CONFIG,
            t,
            companyEnabled,
            company,
            savedCompany,
        ],
    );

    // Paiement en un clic d'un membre connecté : l'abonnement est déjà créé,
    // et sa première facture ne peut plus changer de destinataire. Passer à
    // une entreprise annule donc cette tentative et rouvre le formulaire,
    // interrupteur activé ; le paiement repart de là avec l'entreprise.
    const onSwitchToCompanyForm = () => {
        if (pendingSubscriptionId && clientSecret) {
            cancelPendingSubscription(pendingSubscriptionId);
        }
        checkoutGenerationRef.current += 1;
        setClientSecret(null);
        setPendingSubscriptionId(null);
        if (savedCompany?.country) {
            setAddressCountry(savedCompany.country);
            setAddressPostal(savedCompany.postalCode || "");
        }
        setCompanyEnabled(true);
        setFormError(null);
    };

    // Handle v2 reCAPTCHA submission for create user
    const handleRecaptchaV2Submit = useCallback(async () => {
        if (!recaptchaV2Token || !pendingCreateUser) {
            toast.error(t("signup_recaptcha_required"));
            return;
        }

        setLoading(true);
        nProgress.start();

        try {
            const success = await submitCreateUserWithRecaptcha(
                recaptchaV2Token,
                "v2",
            );

            if (!success) {
                // Reset v2 captcha
                if (recaptchaV2Ref.current) {
                    recaptchaV2Ref.current.reset();
                }
                setRecaptchaV2Token(null);
                return;
            }

            // Reset states
            setShowRecaptchaV2(false);
            setPendingCreateUser(null);
            setRecaptchaV2Token(null);

            // Continue to payment intent
            await continueToPaymentIntent();
        } catch (error) {
            setLoading(false);
            nProgress.done();
            toast.error(t("writer_unknown_error"));
        }
    }, [
        recaptchaV2Token,
        pendingCreateUser,
        submitCreateUserWithRecaptcha,
        continueToPaymentIntent,
        t,
    ]);

    // Formulaire unique du tunnel : identifiants (si visiteur) + facturation,
    // puis paiement. Remplace l'ancien enchaînement en 3 étapes.
    const onSubmitCheckout = async () => {
        if (loading) return;

        const needsAccount = !activeSession?.user;

        // Validation complète côté client avant tout appel réseau
        if (needsAccount) {
            if (!emailAddress || !/\S+@\S+\.\S+/.test(emailAddress)) {
                setFormError(t("pricing_error_invalid_email"));
                return;
            }
            if (!password) {
                setFormError(t("pricing_error_missing_password"));
                return;
            }
            if (!firstName || !lastName) {
                setFormError(t("pricing_error_name"));
                return;
            }
        }
        // Le pays suffit à la TVA Stripe, sauf aux États-Unis et au Canada où
        // il faut un code postal bien formé : ne jamais exiger plus à cette
        // étape (chaque champ coûte de la conversion)
        if (!addressCountry) {
            setFormError(t("pricing_error_missing_address"));
            return;
        }
        const postalCodeError = getPostalCodeError(
            addressCountry,
            billingAddress.addressPostal,
        );
        if (postalCodeError) {
            setFormError(t(postalCodeError));
            return;
        }
        // Entreprise : mêmes règles que le serveur (lib/companyBilling.js),
        // vérifiées avant tout appel réseau
        if (companyEnabled) {
            const checked = validateCompanyBilling({
                ...company,
                country: addressCountry,
                postalCode: billingAddress.addressPostal,
            });
            if (!checked.ok) {
                setFormError(t(checked.errorKey));
                return;
            }
        }
        // Le champ montre le code tel qu'il part (« h3z2y7 » devient « H3Z 2Y7 »)
        if (postalCodeField.visible) {
            setAddressPostal(billingAddress.addressPostal);
        }

        setFormError(null);
        setLoading(true);
        nProgress.start();

        posthog.capture("pricing_checkout_form_submitted", {
            type,
            needsAccount,
            ...intentPropertiesRef.current,
        });

        try {
        if (needsAccount) {
            // L'email correspond-il à un compte existant ?
            const response = await fetch("/api/user/post/check-user", {
                method: "post",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    email: emailAddress,
                    password: password,
                }),
            });

            const fetchedData = await response.json();

            if (!response.ok) {
                setLoading(false);
                nProgress.done();
                toast.error(fetchedData.message);
                return;
            }

            if (fetchedData.isRegistered) {
                // Compte existant : connexion puis mise à jour de l'adresse
                await signIn("credentials", {
                    email: emailAddress,
                    password: password,
                    redirect: false,
                });
                let freshSession = null;
                try {
                    freshSession = await updateSession?.();
                } catch (error) {
                    // Non bloquant
                }

                // Déjà membre Premium : inutile (et dangereux) de continuer
                // vers la création d'un nouvel abonnement.
                if (
                    freshSession?.user?.pro ||
                    freshSession?.user?.essential ||
                    freshSession?.user?.ultimate
                ) {
                    toast.info(t("pricing_already_premium"));
                    handleCloseModal();
                    return;
                }

                // En mode entreprise, le pays et le code postal saisis sont
                // ceux de l'entreprise : l'adresse du membre n'est pas touchée
                if (!companyEnabled) {
                    const saveResponse = await fetch("/api/sales/save-user", {
                        method: "post",
                        headers: {
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                            ...billingAddress,
                            locale,
                        }),
                    });

                    if (!saveResponse.ok) {
                        const saveData = await saveResponse.json();
                        setLoading(false);
                        nProgress.done();
                        toast.error(saveData.message);
                        return;
                    }
                }
            } else {
                // Nouveau compte : création protégée par reCAPTCHA v3
                if (!executeRecaptcha) {
                    setLoading(false);
                    nProgress.done();
                    toast.error(t("signup_recaptcha_error"));
                    return;
                }

                try {
                    const recaptchaToken =
                        await executeRecaptcha("pricing_signup");
                    const success = await submitCreateUserWithRecaptcha(
                        recaptchaToken,
                        "v3",
                    );

                    if (!success) {
                        // Erreurs et fallback reCAPTCHA v2 gérés en interne
                        return;
                    }
                } catch (error) {
                    setLoading(false);
                    nProgress.done();
                    toast.error(t("signup_recaptcha_error"));
                    return;
                }
            }
        } else if (!companyEnabled) {
            // Connecté : sauvegarde de l'adresse de facturation saisie (pas
            // en mode entreprise : ce serait l'adresse de l'entreprise)
            const response = await fetch("/api/sales/save-user", {
                method: "post",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    ...billingAddress,
                    locale,
                }),
            });

            const fetchedData = await response.json();

            if (!response.ok) {
                setLoading(false);
                nProgress.done();
                toast.error(fetchedData.message);
                return;
            }
        }

            // Continue to payment intent
            await continueToPaymentIntent();
        } catch (error) {
            // Coupure réseau ou réponse invalide : débloquer le tunnel
            setLoading(false);
            nProgress.done();
            toast.error(t("writer_unknown_error"));
        }
    };
    const newMetaEventLog = async () => {
        // Pixel du navigateur et API Conversions d'un seul geste, sous le même
        // identifiant d'événement (le jeton d'accès, lui, ne part jamais dans
        // le navigateur). Le pixel reçoit enfin les mêmes données commerciales
        // que le serveur : il était appelé sans elles.
        sendMetaEvent("ViewContent", {
            content_ids: ["premium"],
            content_name: "pricing",
        });
    };
    useEffect(() => {
        if (!conversionSend) {
            newMetaEventLog();
            setConversionSend(true);
        }
    }, [conversionSend]);

    // Fonction pour fermer le modal et annuler l'abonnement incomplet si nécessaire
    const handleCloseModal = useCallback(async () => {
        // Si on a un abonnement en attente et que le paiement n'est pas complété
        if (pendingSubscriptionId && clientSecret) {
            try {
                await fetch("/api/sales/cancel-incomplete-subscription", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        subscriptionId: pendingSubscriptionId,
                        locale,
                    }),
                });
            } catch (error) {
                console.error("Erreur lors de l'annulation de l'abonnement incomplet:", error);
            }
        }

        // Invalider toute requête de paiement encore en vol
        checkoutGenerationRef.current += 1;

        // Reset les states
        setClientSecret(null);
        setPendingSubscriptionId(null);
        setModalIsOpen(false);
        setPreparingPayment(false);
        setFormError(null);
        setLoading(false);
        setShowRecaptchaV2(false);
        setPendingCreateUser(null);
        setRecaptchaV2Token(null);
    }, [pendingSubscriptionId, clientSecret, locale]);

    useEffect(() => {
        if (modalIsOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "unset";
        }
    }, [modalIsOpen]);

    // Fermeture du modal au clavier (Échap)
    useEffect(() => {
        if (!modalIsOpen) return;
        const onKeyDown = (event) => {
            if (event.key === "Escape") {
                handleCloseModal();
            }
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [modalIsOpen, handleCloseModal]);

    // Types d'offres qui n'ouvrent JAMAIS droit à l'essai gratuit.
    // Doit rester aligné sur le back-end (pages/api/sales/create-payment-intent.js).
    const typeSupportsTrial = ![
        "ultra",
        "essential",
        "essential-annually",
        "africa-committed",
        "africa-monthly",
        WINBACK_CHECKOUT_TYPE,
    ].includes(type);

    // Source de vérité sur l'essai : tant que le serveur n'a pas répondu, on
    // estime via la géo ; dès que le clientSecret existe, son préfixe tranche
    // (seti_ = essai via SetupIntent, pi_ = paiement immédiat). L'UI ne peut
    // plus promettre "rien à payer aujourd'hui" quand le serveur débite.
    const estimatedTrial = premiumTrialAvailable && typeSupportsTrial;
    const actualIsTrial = clientSecret
        ? clientSecret.startsWith("seti_")
        : null;
    const showTrialUi = actualIsTrial ?? estimatedTrial;

    // Prix de base affiché sur la carte Pro selon le mode de facturation
    // (la promo flash est déjà neutralisée si une campagne influenceur est active)
    const proBasePrice =
        billingPeriod === "monthly"
            ? PROMO_CONFIG.prices.getMonthlyPrice()
            : annuallyIsMonthlyBilling
              ? PROMO_CONFIG.prices.getAnnuallyPrice()
              : PROMO_CONFIG.prices.getAnnualTotalPrice();

    // Formule produit du type sélectionné dans le tunnel (ciblage campagne)
    const TYPE_TO_PLAN = {
        essential: "plus",
        "essential-annually": "plus",
        ultra: "founder",
        monthly: "pro",
        annually: "pro",
        "6-months": "pro",
        "africa-monthly": "pro",
        "africa-committed": "pro",
    };
    const purchasedPlan = TYPE_TO_PLAN[type] || "pro";
    // L'offre de retour ne se cumule avec aucune campagne (le serveur non plus)
    const campaignAppliesToType =
        type !== WINBACK_CHECKOUT_TYPE && planHasCampaign(purchasedPlan);

    // Prix affiché dans le récapitulatif du tunnel (remise campagne comprise :
    // ce que Stripe facturera réellement grâce au coupon percent_off,
    // uniquement si la formule est couverte par la campagne)
    const effectivePrice = campaignAppliesToType ? applyCampaign(price) : price;

    let content = (
        <>
            <div className="min-h-screen bg-white dark:bg-grayDark-950">
                {/* Hero compact : /pricing est une page de décision, les
                    offres sont visibles dès le premier écran (modèle Uxcel) */}
                <section className="pt-14 lg:pt-20 pb-10 lg:pb-14 px-4">
                    <div className="max-w-3xl mx-auto text-center">
                        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-gray-900 dark:text-white mb-5 leading-[1.1] tracking-tight">
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: t.raw(heroTitleKey),
                                }}
                            />
                        </h1>
                        <p className="text-lg lg:text-xl text-gray-500 dark:text-grayDark-400 mb-0 leading-relaxed">
                            {t(heroSubtitleKey)}
                        </p>
                        {showIntentPicker && (
                            <PricingIntentPicker
                                value={intent}
                                onChange={handleIntentChange}
                            />
                        )}
                    </div>
                </section>

                {/* Offers Section */}
                <section className="pb-16 sm:pb-20 px-4 sm:px-6 lg:px-8">
                    <div className="max-w-7xl mx-auto">
                        {/* Deux cartes : Plus (4,99 €, micro-leçons Reps) sert
                            d'ancre de prix, Premium reste l'offre dominante.
                            Starter est un lien texte, son détail vit dans le
                            comparatif */}
                        <div className="grid lg:grid-cols-[1fr_1.3fr_1fr] lg:grid-rows-[auto_auto_auto_auto_auto_1fr] gap-6 max-w-5xl mx-auto items-stretch">
                            {/* Plus : micro-leçons Reps uniquement (ancre) */}
                            <div className="w-full bg-white dark:bg-grayDark-900 rounded-2xl border border-gray-200 dark:border-grayDark-800 p-6 order-2 lg:order-1 lg:grid lg:grid-rows-subgrid lg:row-span-6 lg:gap-y-0">
                                    <div className="flex items-center justify-between gap-2 mb-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-gray-100 dark:bg-grayDark-800 rounded-xl flex items-center justify-center text-gray-500 dark:text-grayDark-400">
                                                <PixelIcon
                                                    name="zap"
                                                    className="w-5 h-5"
                                                />
                                            </div>
                                            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                                                Plus
                                            </h3>
                                        </div>
                                        <span className="shrink-0 whitespace-nowrap rounded-full bg-gray-100 dark:bg-grayDark-800 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-grayDark-400">
                                            {t("pricing_plus_badge")}
                                        </span>
                                    </div>

                                    <p className="text-gray-500 dark:text-grayDark-400 text-sm mb-6">
                                        {t("pricing_plus_description")}
                                    </p>

                                    <div className="mb-6 lg:flex lg:flex-col lg:justify-end">
                                        <div className="flex items-baseline gap-2">
                                            {planHasCampaign("plus") && (
                                                <span className="text-lg text-gray-500 dark:text-grayDark-400 line-through">
                                                    {formatPrice(
                                                        process.env
                                                            .BELIEVEMY_ESSENTIAL_MONTHLY_PRICE,
                                                    )}€
                                                </span>
                                            )}
                                            <span className="text-4xl font-bold text-gray-900 dark:text-white">
                                                {formatPrice(
                                                    applyCampaignTo(
                                                        "plus",
                                                        process.env
                                                            .BELIEVEMY_ESSENTIAL_MONTHLY_PRICE,
                                                    ),
                                                )}
                                            </span>
                                            <span className="text-gray-400">
                                                {t("pricing_monthly_price_euro")}
                                            </span>
                                        </div>
                                        <p className="text-sm mt-1 mb-0 h-5">
                                            <span className="text-gray-500 dark:text-grayDark-400">
                                                {t("pricing_premium_no_commitment")}
                                            </span>
                                        </p>
                                    </div>

                                <Button
                                    className="w-full"
                                    type="secondary"
                                    onClick={() => onStartPremium("essential")}
                                >
                                    {t("pricing_plus_cta")}
                                </Button>
                                <div>
                                <button
                                    type="button"
                                    onClick={() =>
                                        onStartPremium("essential-annually")
                                    }
                                    className="mt-3 text-center text-xs text-gray-500 dark:text-grayDark-400 hover:text-primary dark:hover:text-primary underline underline-offset-4 transition-colors"
                                >
                                    {t("pricing_plus_annual_hint", {
                                        price: formatPrice(
                                            applyCampaignTo(
                                                "plus",
                                                plusAnnualTotal,
                                            ),
                                        ),
                                        monthly: formatPrice(
                                            applyCampaignTo(
                                                "plus",
                                                plusAnnualTotal,
                                            ) / 12,
                                        ),
                                    })}
                                </button>
                                {planHasCampaign("plus") && (
                                    <p className="mt-2 text-center text-xs text-gray-500 dark:text-grayDark-400 mb-0">
                                        {t("pricing_offer_monthly_line", {
                                            price: formatPrice(
                                                applyCampaignTo(
                                                    "plus",
                                                    process.env
                                                        .BELIEVEMY_ESSENTIAL_MONTHLY_PRICE,
                                                ),
                                            ),
                                            renewal: formatPrice(
                                                process.env
                                                    .BELIEVEMY_ESSENTIAL_MONTHLY_PRICE,
                                            ),
                                        })}
                                    </p>
                                )}
                                </div>
                                                            <div className="mt-6 space-y-3 border-t border-gray-100 dark:border-grayDark-800 pt-6">
                                    {[
                                        t("pricing_essential_point1"),
                                        t("pricing_essential_point2"),
                                        ...(repsAssistantIncludedInPlus()
                                            ? [
                                                  t(
                                                      "pricing_essential_point_bebot",
                                                  ),
                                              ]
                                            : []),
                                        ...(isRepsAppStoreLive()
                                            ? [
                                                  t(
                                                      "pricing_essential_point_app",
                                                  ),
                                              ]
                                            : []),
                                    ].map((feature, i) => (
                                        <div
                                            key={i}
                                            className="flex items-start gap-3"
                                        >
                                            <SolarIcon
                                                name="check-circle-bold"
                                                className="w-5 h-5 text-gray-400 dark:text-grayDark-500 flex-shrink-0 mt-0.5"
                                            />
                                            <span className="text-gray-600 dark:text-grayDark-300 text-sm">
                                                {feature}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Premium - Most Popular */}
                            <div className="w-full bg-gray-50 dark:bg-grayDark-900 rounded-2xl p-6 relative shadow-xl dark:shadow-black/40 border border-gray-100 dark:border-grayDark-800 order-1 lg:order-2 lg:grid lg:grid-rows-subgrid lg:row-span-6 lg:gap-y-0">
                                <ShineBorder
                                    shineColor={[
                                        "#E8511D",
                                        "#FF8A4C",
                                        "#FDBA74",
                                    ]}
                                    borderWidth={2.5}
                                />
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-primary/20 rounded-xl flex items-center justify-center text-primary">
                                                <PixelIcon
                                                    name="book-open"
                                                    className="w-5 h-5"
                                                />
                                            </div>
                                            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                                                Pro
                                            </h3>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {!isAfricaPricing &&
                                                billingPeriod === "annually" && (
                                                    <span className="rounded-full bg-primary/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-primary">
                                                        {t("pricing_most_popular")}
                                                    </span>
                                                )}
                                            {!isAfricaPricing && PROMO_CONFIG.isActive &&
                                                billingPeriod === "annually" && (
                                                    <span className="text-sm font-medium text-gray-400 line-through">
                                                        {formatPrice(
                                                            annuallyIsMonthlyBilling
                                                                ? PROMO_CONFIG.prices.annuallyBase
                                                                : PROMO_CONFIG.prices.getAnnualBaseTotalPrice(),
                                                        )}
                                                        €
                                                    </span>
                                                )}
                                        </div>
                                    </div>

                                    <p className="text-gray-500 dark:text-grayDark-400 text-sm mb-6">
                                        {t("pricing_premium_description")}
                                    </p>

                                    {/* Rangée prix (sous-grille) : toggle +
                                        prix + bonus, calés au-dessus du CTA */}
                                    <div className="lg:flex lg:flex-col lg:justify-end">
                                    {/* Price Switcher — toggle facon Uxcel */}
                                    <div className="mb-5">
                                        {isAfricaPricing ? (
                                            <div className="inline-flex items-center rounded-full bg-gray-100 dark:bg-grayDark-800 p-0.5 text-xs font-semibold">
                                                <button
                                                    className={`rounded-full px-3 py-1.5 transition-colors ${
                                                        billingPeriod === "africa-committed"
                                                            ? "bg-white dark:bg-grayDark-700 text-gray-900 dark:text-white shadow-sm"
                                                            : "text-gray-500 dark:text-grayDark-400 hover:text-gray-700 dark:hover:text-grayDark-200"
                                                    }`}
                                                    onClick={() =>
                                                        setBillingPeriod("africa-committed")
                                                    }
                                                >
                                                    {t("pricing_africa_committed")}
                                                </button>
                                                <button
                                                    className={`rounded-full px-3 py-1.5 transition-colors ${
                                                        billingPeriod === "africa-monthly"
                                                            ? "bg-white dark:bg-grayDark-700 text-gray-900 dark:text-white shadow-sm"
                                                            : "text-gray-500 dark:text-grayDark-400 hover:text-gray-700 dark:hover:text-grayDark-200"
                                                    }`}
                                                    onClick={() =>
                                                        setBillingPeriod("africa-monthly")
                                                    }
                                                >
                                                    {t("pricing_africa_no_commitment")}
                                                </button>
                                            </div>
                                        ) : (
                                            <button
                                                type="button"
                                                role="switch"
                                                aria-checked={billingPeriod === "annually"}
                                                onClick={() =>
                                                    setBillingPeriod(
                                                        billingPeriod === "annually"
                                                            ? "monthly"
                                                            : "annually",
                                                    )
                                                }
                                                className="flex items-center gap-2.5"
                                            >
                                                <span
                                                    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                                                        billingPeriod === "annually"
                                                            ? "bg-primary"
                                                            : "bg-gray-300 dark:bg-grayDark-600"
                                                    }`}
                                                >
                                                    <span
                                                        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                                                            billingPeriod === "annually"
                                                                ? "translate-x-[22px]"
                                                                : "translate-x-0.5"
                                                        }`}
                                                    />
                                                </span>
                                                <span className="text-sm font-medium text-gray-600 dark:text-grayDark-300">
                                                    {/* Mensualisé : on paie chaque mois, « Facturé
                                                        annuellement » serait faux. L'étiquette dit
                                                        alors ce qu'on accepte en basculant. */}
                                                    {annuallyIsMonthlyBilling
                                                        ? t("pricing_engagement_months", {
                                                              months: 12,
                                                          })
                                                        : t("pricing_billed_yearly")}
                                                    {/* Le % n'apparaît qu'en mensuel (incitation
                                                        à basculer) : en annuel, la sous-ligne du
                                                        prix l'affiche déjà, campagne incluse */}
                                                    {billingPeriod === "monthly" && (
                                                        <>
                                                            <span className="mx-1 text-gray-300 dark:text-grayDark-600">
                                                                ·
                                                            </span>
                                                            <span className="text-emerald-600 dark:text-emerald-400">
                                                                {t("pricing_save_percent", {
                                                                    percent: Math.round(
                                                                        ((PROMO_CONFIG.prices.getMonthlyPrice() -
                                                                            PROMO_CONFIG.prices.getAnnuallyPrice()) /
                                                                            PROMO_CONFIG.prices.getMonthlyPrice()) *
                                                                            100,
                                                                    ),
                                                                })}
                                                            </span>
                                                        </>
                                                    )}
                                                </span>
                                            </button>
                                        )}
                                    </div>

                                    <div className="mb-6">
                                        {!isAfricaPricing && PROMO_CONFIG.isActive &&
                                            billingPeriod === "annually" && (
                                                <div className="mb-2 flex flex-wrap items-center gap-2">
                                                    <span className="inline-block bg-red-500 text-white text-xs font-semibold px-2.5 py-1 rounded-full">
                                                        {t(
                                                            "pricing_premium_annually_price_promotion",
                                                            {
                                                                reduction:
                                                                    PROMO_CONFIG.discount,
                                                            },
                                                        )}
                                                    </span>
                                                    {promoCountdown && (
                                                        <span className="text-xs text-gray-400 font-mono">
                                                            {t("pricing_promo_ends_in", {
                                                                time: promoCountdown,
                                                            })}
                                                        </span>
                                                    )}
                                                    <p className="w-full text-[11px] text-gray-400 dark:text-grayDark-500 mb-0">
                                                        {annuallyIsMonthlyBilling
                                                            ? t(
                                                                  "pricing_offer_monthly_line",
                                                                  {
                                                                      price: formatPrice(
                                                                          PROMO_CONFIG.prices.getAnnuallyPrice(),
                                                                      ),
                                                                      renewal: formatPrice(
                                                                          PROMO_CONFIG
                                                                              .prices
                                                                              .annuallyBase,
                                                                      ),
                                                                  },
                                                              )
                                                            : t(
                                                                  "pricing_offer_year_line",
                                                                  {
                                                                      price: formatPrice(
                                                                          PROMO_CONFIG.prices.getAnnualTotalPrice(),
                                                                      ),
                                                                      original:
                                                                          formatPrice(
                                                                              PROMO_CONFIG.prices.getAnnualBaseTotalPrice(),
                                                                          ),
                                                                      renewal: formatPrice(
                                                                          PROMO_CONFIG.prices.getAnnualBaseTotalPrice() /
                                                                              12,
                                                                      ),
                                                                  },
                                                              )}
                                                    </p>
                                                </div>
                                            )}

                                        {isAfricaPricing ? (
                                            <>
                                                <div className="flex items-baseline gap-2">
                                                    <span className="text-4xl font-bold text-gray-900 dark:text-white">
                                                        {billingPeriod === "africa-committed"
                                                            ? (africaCommittedIsMonthlyBilling
                                                                ? process.env.BELIEVEMY_PREMIUM_AFRICA_COMMITTED_PRICE
                                                                : formatPrice(parseFloat(process.env.BELIEVEMY_PREMIUM_AFRICA_COMMITTED_PRICE) * 12))
                                                            : process.env.BELIEVEMY_PREMIUM_AFRICA_MONTHLY_PRICE}
                                                    </span>
                                                    <span className="text-gray-400">
                                                        {t(
                                                            billingPeriod === "africa-committed" && !africaCommittedIsMonthlyBilling
                                                                ? "pricing_annually_price_euro"
                                                                : "pricing_africa_monthly_price_euro",
                                                        )}
                                                    </span>
                                                </div>
                                                <p className="text-sm mt-1 mb-0 h-5">
                                                    <span className="text-gray-500 dark:text-grayDark-400">
                                                        {billingPeriod === "africa-committed"
                                                            ? t("pricing_africa_committed_description")
                                                            : t("pricing_africa_no_commitment_description")}
                                                    </span>
                                                </p>
                                            </>
                                        ) : (
                                            <>
                                                {/* Façon Hostinger : équivalent mensuel en
                                                    grand, vrai prix mensuel barré, total
                                                    annuel en clair dans la sous-ligne, détail
                                                    de l'offre sous le CTA */}
                                                <div className="flex items-baseline gap-2">
                                                    {(billingPeriod === "annually" ||
                                                        planHasCampaign("pro")) && (
                                                        <span className="text-lg text-gray-500 dark:text-grayDark-400 line-through">
                                                            {formatPrice(
                                                                PROMO_CONFIG.prices.getMonthlyPrice(),
                                                            )}
                                                            €
                                                        </span>
                                                    )}
                                                    <span className="text-4xl font-bold text-gray-900 dark:text-white">
                                                        {formatPrice(
                                                            applyCampaignTo(
                                                                "pro",
                                                                billingPeriod ===
                                                                    "monthly"
                                                                    ? PROMO_CONFIG.prices.getMonthlyPrice()
                                                                    : PROMO_CONFIG.prices.getAnnuallyPrice(),
                                                            ),
                                                        )}
                                                    </span>
                                                    <span className="text-gray-400">
                                                        {t(
                                                            "pricing_monthly_price_euro",
                                                        )}
                                                    </span>
                                                </div>

                                                <p className="text-sm mt-1 mb-0 h-5">
                                                    {billingPeriod ===
                                                    "monthly" ? (
                                                        <span className="text-gray-500 dark:text-grayDark-400">
                                                            {t(
                                                                "pricing_premium_no_commitment",
                                                            )}
                                                        </span>
                                                    ) : (
                                                        <>
                                                            <span className="text-gray-500 dark:text-grayDark-400">
                                                                {annuallyIsMonthlyBilling
                                                                    ? t(
                                                                          "pricing_engagement_months",
                                                                          {
                                                                              months: 12,
                                                                          },
                                                                      )
                                                                    : t(
                                                                          "pricing_billed_annually_total",
                                                                          {
                                                                              total: formatPrice(
                                                                                  applyCampaignTo(
                                                                                      "pro",
                                                                                      PROMO_CONFIG.prices.getAnnualTotalPrice(),
                                                                                  ),
                                                                              ),
                                                                          },
                                                                      )}
                                                            </span>
                                                            <span className="text-gray-500 dark:text-grayDark-400">
                                                                {" "}
                                                                ·{" "}
                                                            </span>
                                                            <span className="text-emerald-600 dark:text-emerald-400 font-medium">
                                                                {t(
                                                                    "pricing_save_percent",
                                                                    {
                                                                        percent: Math.round(
                                                                            (1 -
                                                                                applyCampaignTo(
                                                                                    "pro",
                                                                                    PROMO_CONFIG.prices.getAnnuallyPrice(),
                                                                                ) /
                                                                                    PROMO_CONFIG.prices.getMonthlyPrice()) *
                                                                                100,
                                                                        ),
                                                                    },
                                                                )}
                                                            </span>
                                                        </>
                                                    )}
                                                </p>
                                            </>
                                        )}
                                    </div>

                                    {/* Free call bonus for annual subscribers */}
                                    {!isAfricaPricing && FREE_CALL_CONFIG.isActive &&
                                        billingPeriod === "annually" && (
                                            <div className="mb-6 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-xl">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-8 h-8 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                                                        <SolarIcon
                                                            name="phone-rounded-bold"
                                                            className="w-4 h-4 text-emerald-600 dark:text-emerald-400"
                                                        />
                                                    </div>
                                                    <div className="flex-1">
                                                        <p className="text-emerald-600 dark:text-emerald-400 text-sm font-semibold mb-0">
                                                            {t("pricing_free_call_bonus")}
                                                        </p>
                                                        <p className="text-emerald-600/70 dark:text-emerald-400/70 text-xs mb-0">
                                                            {t("pricing_free_call_with")}
                                                        </p>
                                                        <p className="text-gray-500 dark:text-grayDark-400 text-xs mb-0 mt-1">
                                                            {t("pricing_free_call_value", {
                                                                value: FREE_CALL_CONFIG.value,
                                                            })}{" "}
                                                            ·{" "}
                                                            {t("pricing_free_call_until", {
                                                                date: FREE_CALL_CONFIG.getFormattedEndDate(locale),
                                                            })}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                </div>

                                <Button
                                    className="w-full"
                                    type="premium-price"
                                    onClick={() => {
                                        if (isAfricaPricing) {
                                            onStartPremium(billingPeriod);
                                        } else {
                                            billingPeriod === "annually"
                                                ? onStartPremium("annually")
                                                : onStartPremium("monthly");
                                        }
                                    }}
                                >
                                    {!premiumTrialAvailable
                                        ? t("pricing_button_startNow")
                                        : t("pricing_button_startForFreeDays", {
                                              freeDays:
                                                  pricingTrial.days,
                                          })}
                                </Button>
                                <div>
                                {/* Une seule réassurance : avec essai, on ne paie
                                    rien aujourd'hui (la garantie 14 j ferait
                                    doublon) ; sans essai, la garantie prend le
                                    relais */}
                                {!premiumTrialAvailable && (
                                    <div className="mt-2 flex items-center justify-center gap-2 rounded-xl bg-gray-100 dark:bg-grayDark-800 py-2.5 px-3 text-xs font-medium text-gray-500 dark:text-grayDark-400">
                                        <SolarIcon name="shield-check-bold" className="w-4 h-4 shrink-0" />
                                        {t("pricing_guarantee_14days")}
                                    </div>
                                )}
                                {planHasCampaign("pro") && (
                                    <p className="mt-2 text-center text-xs text-gray-500 dark:text-grayDark-400 mb-0">
                                        {billingPeriod === "monthly" ||
                                        annuallyIsMonthlyBilling
                                            ? t("pricing_offer_monthly_line", {
                                                  price: formatPrice(
                                                      applyCampaignTo(
                                                          "pro",
                                                          proBasePrice,
                                                      ),
                                                  ),
                                                  renewal:
                                                      formatPrice(proBasePrice),
                                              })
                                            : t("pricing_offer_year_line", {
                                                  price: formatPrice(
                                                      applyCampaignTo(
                                                          "pro",
                                                          proBasePrice,
                                                      ),
                                                  ),
                                                  original:
                                                      formatPrice(proBasePrice),
                                                  renewal: formatPrice(
                                                      proBasePrice / 12,
                                                  ),
                                              })}
                                    </p>
                                )}
                                {premiumTrialAvailable && (
                                    <p className="text-center text-xs text-gray-500 dark:text-grayDark-400 mt-2 mb-0">
                                        {t("pricing_trial_reassurance")}
                                    </p>
                                )}
                                </div>
                                                            <div className="mt-6 space-y-3 border-t border-gray-100 dark:border-grayDark-800 pt-6">
                                    {/* Ordre et libellés dictés par l'intention :
                                        l'argument qui sert l'objectif passe en tête */}
                                    {premiumFeatures.map((feature) => (
                                        <div
                                            key={feature.id}
                                            className="flex items-start gap-3"
                                        >
                                            <SolarIcon
                                                name="check-circle-bold"
                                                className="w-5 h-5 text-primary flex-shrink-0 mt-0.5"
                                            />
                                            <span className="text-gray-600 dark:text-grayDark-300 text-sm">
                                                {t(feature.labelKey, {
                                                    numberOfCourses:
                                                        process.env
                                                            .NUMBER_OF_COURSES,
                                                })}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Founder : ancre haute, mentorat direct avec le
                                fondateur (rareté réelle : son temps) */}
                            <div className="w-full bg-white dark:bg-grayDark-900 rounded-2xl border border-gray-200 dark:border-grayDark-800 p-6 order-3 lg:grid lg:grid-rows-subgrid lg:row-span-6 lg:gap-y-0">
                                    <div className="flex items-center justify-between gap-2 mb-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-primary-100/10 rounded-xl flex items-center justify-center text-primary-100/70">
                                                <PixelIcon
                                                    name="crown"
                                                    className="w-5 h-5"
                                                />
                                            </div>
                                            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                                                Founder
                                            </h3>
                                        </div>
                                        <span className="shrink-0 whitespace-nowrap rounded-full bg-primary-100/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-primary-100/80">
                                            {t(
                                                "account_subscriptions_founder_badge",
                                            )}
                                        </span>
                                    </div>

                                    <p className="text-gray-500 dark:text-grayDark-400 text-sm mb-6">
                                        {t("pricing_founder_card_description")}
                                    </p>

                                    <div className="mb-6 lg:flex lg:flex-col lg:justify-end">
                                        <div className="flex items-baseline gap-2">
                                            {planHasCampaign("founder") && (
                                                <span className="text-lg text-gray-500 dark:text-grayDark-400 line-through">
                                                    {formatPrice(
                                                        process.env
                                                            .BELIEVEMY_ULTRA_MONTHLY_PRICE,
                                                    )}€
                                                </span>
                                            )}
                                            <span className="text-4xl font-bold text-gray-900 dark:text-white">
                                                {formatPrice(
                                                    applyCampaignTo(
                                                        "founder",
                                                        process.env
                                                            .BELIEVEMY_ULTRA_MONTHLY_PRICE,
                                                    ),
                                                )}
                                            </span>
                                            <span className="text-gray-400">
                                                {t("pricing_monthly_price_euro")}
                                            </span>
                                        </div>
                                        <p className="text-sm mt-1 mb-0 h-5">
                                            <span className="text-gray-500 dark:text-grayDark-400">
                                                {t("pricing_premium_no_commitment")}
                                            </span>
                                        </p>
                                    </div>

                                <Button
                                    className="w-full"
                                    type="secondary"
                                    onClick={() => onStartPremium("ultra")}
                                >
                                    {t("pricing_founder_card_cta")}
                                </Button>
                                <div>
                                {/* "Sans engagement" est déjà sous le prix : pas
                                    de répétition sous le CTA */}
                                {planHasCampaign("founder") && (
                                    <p className="mt-2 text-center text-xs text-gray-500 dark:text-grayDark-400 mb-0">
                                        {t("pricing_offer_monthly_line", {
                                            price: formatPrice(
                                                applyCampaignTo(
                                                    "founder",
                                                    process.env
                                                        .BELIEVEMY_ULTRA_MONTHLY_PRICE,
                                                ),
                                            ),
                                            renewal: formatPrice(
                                                process.env
                                                    .BELIEVEMY_ULTRA_MONTHLY_PRICE,
                                            ),
                                        })}
                                    </p>
                                )}
                                </div>
                                                            <div className="mt-6 space-y-3 border-t border-gray-100 dark:border-grayDark-800 pt-6">
                                    {[
                                        t("pricing_founder_card_session"),
                                        t("pricing_founder_benefit_premium"),
                                        t("pricing_founder_benefit_priority"),
                                        t("pricing_founder_benefit_badge"),
                                    ].map((feature, i) => (
                                        <div
                                            key={i}
                                            className="flex items-start gap-3"
                                        >
                                            <SolarIcon
                                                name="check-circle-bold"
                                                className="w-5 h-5 text-primary-100/60 flex-shrink-0 mt-0.5"
                                            />
                                            <span className="text-gray-600 dark:text-grayDark-300 text-sm">
                                                {feature}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Starter : sortie discrète vers le gratuit, sans
                            concurrencer visuellement Premium. Disparaît quand
                            l'offre gratuite est coupée depuis
                            /admin/promotions : ne pas inviter à une formule
                            qui ne donne plus accès à rien. */}
                        {freeOfferEnabled && (
                        <div className="mt-6 text-center max-w-xl mx-auto">
                            {activeSession?.user && !activeSession.user.pro ? (
                                <p className="text-sm text-gray-500 dark:text-grayDark-400 mb-0">
                                    {t("pricing_starter_current_hint")}
                                </p>
                            ) : (
                                <p className="text-sm text-gray-500 dark:text-grayDark-400 mb-0">
                                    {t("pricing_starter_inline")}{" "}
                                    <Link
                                        href="/signup"
                                        className="font-medium text-gray-700 dark:text-grayDark-200 underline underline-offset-4 hover:text-primary dark:hover:text-primary transition-colors"
                                    >
                                        {t("pricing_starter_inline_cta")}
                                    </Link>
                                </p>
                            )}
                        </div>
                        )}

                        {/* Bande de confiance au point de décision */}
                        <div className="mt-12 flex flex-col items-center gap-6">
                            <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-gray-600 dark:text-grayDark-300">
                                <span className="inline-flex items-center gap-2">
                                    <SolarIcon
                                        name="users-group-rounded-bold"
                                        className="w-4 h-4 text-primary"
                                    />
                                    {t(
                                        compteExact
                                            ? "pricing_trust_students_exact"
                                            : "pricing_trust_students",
                                        {
                                        count: Number(nombreCommunaute).toLocaleString(locale),
                                    })}
                                </span>
                                <span className="inline-flex items-center gap-2">
                                    <span className="flex gap-0.5">
                                        {[0, 1, 2, 3, 4].map((i) => (
                                            <SolarIcon
                                                key={i}
                                                name="star-bold"
                                                className="w-3.5 h-3.5 text-amber-400"
                                            />
                                        ))}
                                    </span>
                                    {t("pricing_trust_rating", {
                                        note: Number(
                                            process.env.TRUSTPILOT_NOTE,
                                        ).toLocaleString(locale),
                                    })}
                                </span>
                                <span className="inline-flex items-center gap-2">
                                    <SolarIcon
                                        name="notebook-bookmark-bold"
                                        className="w-4 h-4 text-primary"
                                    />
                                    {t("pricing_trust_courses", {
                                        count: process.env.NUMBER_OF_COURSES,
                                    })}
                                </span>
                            </div>
                            <div className="flex items-center gap-5 sm:gap-7 flex-wrap justify-center">
                                {trustedLogos.slice(0, 4).map((logo, i) => (
                                    <Image
                                        key={i}
                                        src={logo.src}
                                        alt={logo.alt}
                                        width={80}
                                        height={18}
                                        className="object-contain h-[14px] sm:h-[16px] brightness-0 dark:brightness-100 opacity-50 w-auto"
                                    />
                                ))}
                            </div>
                        </div>
                    </div>

                        {/* Liens secondaires : cadeau + équipes */}
                        <div className="mt-10 flex items-center justify-center gap-x-10 gap-y-3 flex-wrap">
                            <Link
                                href="/gift"
                                className="inline-flex items-center gap-2 text-gray-500 dark:text-grayDark-400 hover:text-primary dark:hover:text-primary transition-colors group"
                            >
                                <SolarIcon
                                    name="gift-linear"
                                    className="w-5 h-5"
                                />
                                <span className="text-sm font-medium">
                                    {t("pricing_gift_link")}
                                </span>
                                <SolarIcon
                                    name="arrow-right-linear"
                                    className="w-4 h-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all"
                                />
                            </Link>
                            <Link
                                href="/teams"
                                className="inline-flex items-center gap-2 text-gray-500 dark:text-grayDark-400 hover:text-primary dark:hover:text-primary transition-colors group"
                            >
                                <SolarIcon
                                    name="users-group-two-rounded-linear"
                                    className="w-5 h-5"
                                />
                                <span className="text-sm font-medium">
                                    {t("pricing_teams_link")}
                                </span>
                                <SolarIcon
                                    name="arrow-right-linear"
                                    className="w-4 h-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all"
                                />
                            </Link>
                        </div>
                </section>

                {/* Parcours réel de la personne : la preuve concrète que ce
                    qu'elle veut apprendre est dans l'abonnement */}
                <PricingPersonalPath
                    path={personalPath}
                    goalLabelKey={goalLabelKey}
                    otherCoursesCount={otherCoursesCount}
                    onStart={() => {
                        // Même résolution que le CTA de la carte Premium :
                        // les tarifs Afrique ont leurs propres types
                        if (isAfricaPricing) {
                            onStartPremium(billingPeriod);
                            return;
                        }
                        onStartPremium(
                            billingPeriod === "annually"
                                ? "annually"
                                : "monthly",
                        );
                    }}
                />

                {/* Comparatif Starter / Plus / Premium : le détail des offres
                    vit ici, sous les cartes, pas en concurrence avec elles */}
                <section className="py-14 sm:py-20 px-4">
                    <div className="max-w-4xl mx-auto">
                        <h2 className="md:text-5xl text-4xl dark:text-white font-bold mb-5 lg:text-center">
                            {t("pricing_comparative")}
                        </h2>

                        <p className="dark:text-white text-lg lg:text-center">
                            {t("pricing_comparativeDescription")}
                        </p>

                        <div className="overflow-x-auto lg:mt-10 mt-5 rounded-xl">
                            <table className="w-full border-collapse text-sm">
                                <thead>
                                    <tr className="bg-gray-100 dark:bg-grayDark-800 text-gray-700 dark:text-white">
                                        <th className="p-4 text-left border-b dark:border-borderDark"></th>
                                        {freeOfferEnabled && (
                                            <th className="p-4 text-center border-b dark:border-borderDark">
                                                Starter
                                            </th>
                                        )}
                                        <th className="p-4 text-center border-b dark:border-borderDark">
                                            Plus
                                        </th>
                                        <th className="p-4 text-center border-b dark:border-borderDark">
                                            Pro
                                        </th>
                                        <th className="p-4 text-center border-b dark:border-borderDark">
                                            Founder
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="text-gray-800 dark:text-white">
                                    {/* Bloc 1 : Formation */}
                                    <tr className="bg-gray-50 dark:bg-grayDark-900 font-semibold">
                                        <td className="p-4" colSpan={freeOfferEnabled ? 5 : 4}>
                                            💼 {t("pricing_formations")}
                                        </td>
                                    </tr>
                                    <TableRow
                                        label={t("pricing_firstParts")}
                                        values={comparisonCols(["✓", "✓", "✓", "✓"])}
                                    />
                                    <TableRow
                                        label={t("pricing_unlimitedAccess")}
                                        values={comparisonCols(["-", "-", "✓", "✓"])}
                                    />
                                    <TableRow
                                        label={t("pricing_table_reps")}
                                        values={comparisonCols([
                                            t("pricing_table_limited"),
                                            t("pricing_table_unlimited"),
                                            t("pricing_table_unlimited"),
                                            t("pricing_table_unlimited"),
                                        ])}
                                    />
                                    <TableRow
                                        label={t("pricing_table_bebot")}
                                        values={comparisonCols([
                                            "-",
                                            repsAssistantIncludedInPlus()
                                                ? "✓"
                                                : "-",
                                            "✓",
                                            "✓",
                                        ])}
                                    />
                                    {/* L'app est freemium (3 clés/jour sans
                                        abonnement) : ✓ pour tous, la limite
                                        est déjà portée par la ligne Reps */}
                                    {isRepsAppStoreLive() && (
                                        <TableRow
                                            label={t("pricing_table_app")}
                                            values={comparisonCols(["✓", "✓", "✓", "✓"])}
                                        />
                                    )}
                                    <TableRow
                                        label={t(
                                            "pricing_officialCertifications",
                                        )}
                                        values={comparisonCols(["-", "-", "✓", "✓"])}
                                    />
                                    <TableRow
                                        label={t("pricing_addCertifications")}
                                        values={comparisonCols(["-", "-", "✓", "✓"])}
                                    />
                                    <TableRow
                                        label={t("pricing_challenges")}
                                        values={comparisonCols(["-", "-", "✓", "✓"])}
                                    />
                                    <TableRow
                                        label={t("pricing_table_founder_session")}
                                        values={comparisonCols(["-", "-", "-", "✓"])}
                                    />
                                    {/* Bloc 2 : Services */}
                                    <tr className="bg-gray-50 dark:bg-grayDark-900 font-semibold">
                                        <td className="p-4" colSpan={freeOfferEnabled ? 5 : 4}>
                                            🛠️ {t("pricing_support")}
                                        </td>
                                    </tr>
                                    <TableRow
                                        label={t("pricing_technicalSupport")}
                                        values={comparisonCols(["✓", "✓", "✓", "✓"])}
                                    />
                                    <TableRow
                                        label={t("pricing_priorityResponse")}
                                        values={comparisonCols(["-", "-", "✓", "✓"])}
                                    />
                                    <TableRow
                                        label={t("pricing_premiumEvents")}
                                        values={comparisonCols(["-", "-", "✓", "✓"])}
                                    />
                                    {/* Bloc 3 : Prix */}
                                    <tr className="bg-gray-50 dark:bg-grayDark-900 font-semibold">
                                        <td className="p-4" colSpan={freeOfferEnabled ? 5 : 4}>
                                            💰 {t("pricing_investment")}
                                        </td>
                                    </tr>
                                    <TableRow
                                        label={t("pricing_monthlyCost")}
                                        values={comparisonCols([
                                            t("pricing_free"),
                                            `${formatPrice(
                                                process.env
                                                    .BELIEVEMY_ESSENTIAL_MONTHLY_PRICE,
                                            )} € / ${t("pricing_table_month")}`,
                                            t("pricing_from", {
                                                price: isAfricaPricing
                                                    ? formatPrice(
                                                          process.env
                                                              .BELIEVEMY_PREMIUM_AFRICA_COMMITTED_PRICE,
                                                      )
                                                    : formatPrice(
                                                          PROMO_CONFIG.prices.getAnnuallyPrice(),
                                                      ),
                                            }),
                                            `${formatPrice(
                                                process.env
                                                    .BELIEVEMY_ULTRA_MONTHLY_PRICE,
                                            )} € / ${t("pricing_table_month")}`,
                                        ])}
                                    />
                                </tbody>
                            </table>

                            <p className="mt-4 text-xs text-gray-500 dark:text-grayDark-400">
                                * {t("pricing_premiumEventsDescription")}
                            </p>
                        </div>
                    </div>
                </section>

                {/* VS : la seule section d'argumentaire conservée */}
                <section className="py-14 sm:py-20 px-4">
                    <div className="max-w-6xl mx-auto">
                        {/* Comparison Section */}
                        <div>
                            <div className="text-center mb-10 lg:mb-16">
                                <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 dark:text-white">
                                    {t("wol_whatMakesDifferent")}
                                </h2>
                            </div>

                            <div className="relative grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* VS Badge */}
                                <div className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
                                    <div className="w-14 h-14 rounded-full bg-white dark:bg-grayDark-800 border-2 border-gray-200 dark:border-borderDark flex items-center justify-center shadow-lg">
                                        <span className="text-primary font-bold text-sm">
                                            VS
                                        </span>
                                    </div>
                                </div>

                                {/* Left Card - Old Way */}
                                <div className="bg-white dark:bg-grayDark-900 rounded-2xl lg:rounded-3xl border-2 border-dashed border-gray-200 dark:border-borderDark p-5 lg:p-8">
                                    {/* App Icons with wavy dashed lines */}
                                    <div className="relative flex items-center justify-center mb-8 h-20">
                                        <div className="relative w-full h-32">
                                            <svg
                                                className="absolute inset-0 w-full h-full"
                                                viewBox="0 0 400 100"
                                                preserveAspectRatio="xMidYMid meet"
                                            >
                                                <path
                                                    d="M-20,50 C30,10 70,10 110,50 C150,90 190,90 230,50 C270,10 310,10 350,50 C390,90 430,90 470,50"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="1.5"
                                                    strokeDasharray="6 6"
                                                    className="text-gray-200 dark:text-grayDark-700"
                                                />
                                            </svg>

                                            {/* App icons */}
                                            <div className="absolute left-[10%] top-[15%] w-11 h-11 rounded-xl flex items-center justify-center shadow-lg transform -rotate-6 overflow-hidden">
                                                <Image
                                                    src="/images/site/courses/icon-udemy.png"
                                                    alt="Udemy"
                                                    width={44}
                                                    height={44}
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>
                                            <div className="absolute left-[32%] top-[45%] w-11 h-11 rounded-xl flex items-center justify-center shadow-lg transform rotate-3 overflow-hidden">
                                                <Image
                                                    src="/images/site/courses/icon-stackoverflow.png"
                                                    alt="Stack Overflow"
                                                    width={44}
                                                    height={44}
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>
                                            <div className="absolute left-[55%] top-[10%] w-11 h-11 rounded-xl flex items-center justify-center shadow-lg transform -rotate-3 overflow-hidden">
                                                <Image
                                                    src="/images/site/courses/icon-coursera.png"
                                                    alt="Coursera"
                                                    width={44}
                                                    height={44}
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>
                                            <div className="absolute left-[78%] top-[40%] w-11 h-11 flex items-center justify-center shadow-lg transform rotate-6 rounded-xl overflow-hidden">
                                                <Image
                                                    src="/images/site/courses/icon-youtube.png"
                                                    alt="YouTube"
                                                    width={44}
                                                    height={44}
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <h3 className="text-lg lg:text-xl font-bold text-gray-900 dark:text-white mb-2 lg:mb-3">
                                        {t("wol_oldWayTitle")}
                                    </h3>
                                    <p className="text-gray-500 dark:text-grayDark-400 text-xs lg:text-sm mb-6 lg:mb-8">
                                        {t("wol_oldWaySubtitle")}
                                    </p>

                                    <div className="space-y-3 lg:space-y-4">
                                        {[
                                            t("wol_oldWayPoint1"),
                                            t("wol_oldWayPoint2"),
                                            t("wol_oldWayPoint3"),
                                            t("wol_oldWayPoint4"),
                                            t("wol_oldWayPoint5"),
                                            t("wol_oldWayPoint6"),
                                        ].map((item, i) => (
                                            <div
                                                key={i}
                                                className="flex items-start gap-2 lg:gap-3"
                                            >
                                                <SolarIcon
                                                    name="dislike-bold"
                                                    className="w-4 h-4 lg:w-5 lg:h-5 text-gray-300 dark:text-grayDark-600 flex-shrink-0 mt-0.5"
                                                />
                                                <span className="text-gray-500 dark:text-grayDark-400 text-xs lg:text-sm">
                                                    {item}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Right Card - Believemy */}
                                <div className="bg-white dark:bg-grayDark-900 rounded-2xl lg:rounded-3xl border-2 border-dashed border-gray-200 dark:border-borderDark p-5 lg:p-8">
                                    {/* Believemy Logo with orbital circles */}
                                    <div className="flex items-center justify-center mb-8 relative h-20 overflow-hidden">
                                        <div className="relative w-80 h-80">
                                            <svg
                                                className="absolute inset-0 w-full h-full"
                                                viewBox="0 0 320 320"
                                            >
                                                <circle
                                                    cx="160"
                                                    cy="160"
                                                    r="155"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="1"
                                                    strokeDasharray="4 4"
                                                    className="text-gray-200 dark:text-grayDark-700"
                                                />
                                                <circle
                                                    cx="160"
                                                    cy="160"
                                                    r="115"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="1"
                                                    strokeDasharray="4 4"
                                                    className="text-gray-200 dark:text-grayDark-700"
                                                />
                                                <circle
                                                    cx="160"
                                                    cy="160"
                                                    r="75"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="1"
                                                    strokeDasharray="4 4"
                                                    className="text-gray-200 dark:text-grayDark-700"
                                                />
                                            </svg>

                                            {/* Center logo */}
                                            <div className="absolute inset-0 flex items-center justify-center">
                                                <div className="shadow-xl">
                                                    <Logo icon ratio={1.9} />
                                                </div>
                                            </div>

                                            {/* Floating icons */}
                                            <div className="absolute top-[8%] left-[25%] w-5 h-5 text-gray-400 dark:text-grayDark-500">
                                                <svg
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="1.5"
                                                >
                                                    <path d="M2 11.5c0-3.287 0-4.931.908-6.038a4 4 0 0 1 .554-.554C4.57 4 6.212 4 9.5 4c3.287 0 4.931 0 6.038.908a4 4 0 0 1 .554.554C17 6.57 17 8.212 17 11.5v1c0 3.287 0 4.931-.908 6.038a4 4 0 0 1-.554.554C14.43 20 12.788 20 9.5 20c-3.287 0-4.931 0-6.038-.908a4 4 0 0 1-.554-.554C2 17.43 2 15.788 2 12.5zm15-2l.658-.329c1.946-.973 2.92-1.46 3.63-1.02c.712.44.712 1.528.712 3.703v.292c0 2.176 0 3.263-.711 3.703c-.712.44-1.685-.047-3.63-1.02L17 14.5z" />
                                                </svg>
                                            </div>
                                            <div className="absolute top-[5%] right-[25%] w-5 h-5 text-gray-400 dark:text-grayDark-500">
                                                <svg
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="1.5"
                                                >
                                                    <path d="M3 10c0-3.771 0-5.657 1.172-6.828S7.229 2 11 2h2c3.771 0 5.657 0 6.828 1.172S21 6.229 21 10v4c0 3.771 0 5.657-1.172 6.828S16.771 22 13 22h-2c-3.771 0-5.657 0-6.828-1.172S3 17.771 3 14z" />
                                                    <path
                                                        strokeLinecap="round"
                                                        d="M8 12h8M8 8h8m-8 8h5"
                                                    />
                                                </svg>
                                            </div>
                                            <div className="absolute top-[22%] left-[8%] w-5 h-5 text-gray-400 dark:text-grayDark-500">
                                                <svg
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="1.5"
                                                >
                                                    <circle
                                                        cx="12"
                                                        cy="16"
                                                        r="3"
                                                    />
                                                    <path d="M17.32 17.996c1.972-.021 3.072-.145 3.801-.875C22 16.243 22 14.828 22 12V8c0-2.828 0-4.243-.879-5.121C20.243 2 18.828 2 16 2H8c-2.828 0-4.243 0-5.121.879C2 3.757 2 5.172 2 8v4c0 2.828 0 4.243.879 5.121c.768.769 1.946.865 4.121.877" />
                                                    <path
                                                        strokeLinecap="round"
                                                        d="M9 6h6M7 9.5h10"
                                                    />
                                                </svg>
                                            </div>
                                            <div className="absolute top-[20%] right-[5%] w-5 h-5 text-gray-400 dark:text-grayDark-500">
                                                <svg
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="1.5"
                                                >
                                                    <path d="M4 8c0-2.828 0-4.243.879-5.121C5.757 2 7.172 2 10 2h4c2.828 0 4.243 0 5.121.879C20 3.757 20 5.172 20 8v8c0 2.828 0 4.243-.879 5.121C18.243 22 16.828 22 14 22h-4c-2.828 0-4.243 0-5.121-.879C4 20.243 4 18.828 4 16z" />
                                                    <path d="M19.898 16h-12c-.93 0-1.395 0-1.777.102A3 3 0 0 0 4 18.224" />
                                                    <path
                                                        strokeLinecap="round"
                                                        d="M8 7h8m-8 3.5h5m6.5 8.5H8"
                                                    />
                                                </svg>
                                            </div>

                                            {/* Gradient overlays */}
                                            <div className="absolute inset-x-0 top-0 h-4 bg-gradient-to-b from-white dark:from-grayDark-900 to-transparent pointer-events-none" />
                                            <div className="absolute inset-x-0 bottom-0 h-4 bg-gradient-to-t from-white dark:from-grayDark-900 to-transparent pointer-events-none" />
                                        </div>
                                    </div>

                                    <h3 className="text-lg lg:text-xl font-bold text-gray-900 dark:text-white mb-2 lg:mb-3">
                                        {t("wol_newWayTitle")}
                                    </h3>
                                    <p className="text-gray-500 dark:text-grayDark-400 text-xs lg:text-sm mb-6 lg:mb-8">
                                        {t("wol_newWaySubtitle")}
                                    </p>

                                    <div className="space-y-3 lg:space-y-4">
                                        {[
                                            t("wol_newWayPoint1"),
                                            t("wol_newWayPoint2"),
                                            t("wol_newWayPoint3"),
                                            t("wol_newWayPoint4"),
                                            t("wol_newWayPoint5"),
                                            t("wol_newWayPoint6"),
                                        ].map((item, i) => (
                                            <div
                                                key={i}
                                                className="flex items-start gap-2 lg:gap-3"
                                            >
                                                <SolarIcon
                                                    name="like-bold"
                                                    className="w-4 h-4 lg:w-5 lg:h-5 text-primary flex-shrink-0 mt-0.5"
                                                />
                                                <span className="text-gray-700 dark:text-grayDark-300 text-xs lg:text-sm">
                                                    {item}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Témoignages réels */}
                <section className="px-4">
                    <div className="max-w-7xl mx-auto">
                        <WOL
                            withoutContainer
                            className=""
                            nombreComptes={nombreCommunaute}
                        />
                    </div>
                </section>

                {/* Q&A */}
                    <section className="py-14 sm:py-20 px-4">
                    <div className="max-w-6xl mx-auto">
                        <div className="grid lg:grid-cols-[1fr_2fr] gap-12 lg:gap-16">
                            {/* Left - Header */}
                            <div className="text-left">
                                <p className="text-sm font-semibold text-primary mb-3 tracking-wide">
                                    {t("pricing_faq_tag")}
                                </p>
                                <h2 className="dark:text-white text-3xl sm:text-4xl font-bold">
                                    {t("pricing_questions")}
                                </h2>
                                <p className="dark:text-grayDark-400 text-gray-600 mt-5 text-base leading-relaxed mb-0">
                                    {t("pricing_faq_subtitle")}
                                </p>

                                {/* Contact CTA */}
                                <div className="mt-8">
                                    <p className="text-gray-500 dark:text-grayDark-400 text-sm mb-3">
                                        {t("pricing_faq_still_questions")}
                                    </p>
                                    <Link
                                        href="mailto:hello@believemy.com"
                                        className="inline-flex items-center gap-2 text-primary font-medium hover:underline"
                                    >
                                        {t("pricing_haveAnotherQuestion")}
                                        <SolarIcon name="arrow-right-linear" className="w-4 h-4" />
                                    </Link>
                                </div>
                            </div>

                            {/* Right - Questions */}
                            <div className="flex flex-col gap-3">
                                {faqs.map((faq, index) => (
                                    // Le titre enveloppe le bouton, et non
                                    // l'inverse : les descendants d'un bouton
                                    // sont rendus présentationnels, donc ces
                                    // titres n'en étaient pas. La réponse reste
                                    // dans le DOM (inert quand elle est
                                    // repliée) pour être indexable.
                                    <div key={index} className="group">
                                        <h3 className="mb-0">
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setOpenFaq(
                                                    openFaq === index
                                                        ? null
                                                        : index,
                                                )
                                            }
                                            aria-expanded={openFaq === index}
                                            aria-controls={`pricing-faq-panel-${index}`}
                                            className="w-full text-left"
                                        >
                                            <div
                                                className={`rounded-2xl p-6 border transition-all duration-300 ${
                                                    openFaq === index
                                                        ? "bg-primary text-white border-primary"
                                                        : "bg-white dark:bg-bgDark-840 border-gray-200 dark:border-borderDark hover:border-primary/50"
                                                }`}
                                            >
                                                <div className="flex items-center justify-between gap-4">
                                                    <span
                                                        className={`font-semibold text-base ${
                                                            openFaq === index
                                                                ? "text-white"
                                                                : "text-gray-900 dark:text-white"
                                                        }`}
                                                    >
                                                        {faq.question}
                                                    </span>
                                                    <div
                                                        className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                                                            openFaq === index
                                                                ? "bg-white/20 rotate-180"
                                                                : "bg-gray-100 dark:bg-grayDark-700"
                                                        }`}
                                                    >
                                                        <SolarIcon
                                                            name="alt-arrow-down-linear"
                                                            className={`w-4 h-4 ${
                                                                openFaq ===
                                                                index
                                                                    ? "text-white"
                                                                    : "text-gray-500 dark:text-grayDark-400"
                                                            }`}
                                                        />
                                                    </div>
                                                </div>
                                                <div
                                                    id={`pricing-faq-panel-${index}`}
                                                    inert={openFaq !== index}
                                                    className={`overflow-hidden ${
                                                        openFaq === index
                                                            ? ""
                                                            : "sr-only"
                                                    }`}
                                                >
                                                    <p className="mt-4 text-white/80 text-sm leading-relaxed mb-0">
                                                        {faq.answer}
                                                    </p>
                                                </div>
                                            </div>
                                        </button>
                                        </h3>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                {/* CTA final */}
                <section className="py-20 sm:py-28 px-4">
                    <div className="max-w-3xl mx-auto text-center">
                        <h2 className="font-bold text-4xl sm:text-5xl lg:text-6xl text-gray-900 dark:text-white">
                            {t("EndCTA_default_title")}
                        </h2>
                        <p className="mt-6 text-lg sm:text-xl text-gray-500 dark:text-grayDark-400 mb-0">
                            {t("EndCTA_default_description")}
                        </p>
                        <div className="flex justify-center mt-8">
                            <Button
                                type="pop"
                                onClick={() => onStartPremium(billingPeriod)}
                            >
                                {t("EndCTA_default_button")}
                            </Button>
                        </div>
                        <div className="mt-5 flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-grayDark-400">
                            <SolarIcon
                                name="shield-check-bold"
                                className="w-4 h-4 text-emerald-500"
                            />
                            <p className="mb-0">
                                {t("pricing_guarantee_14days")}
                            </p>
                        </div>
                    </div>
                </section>
            </div>
        </>
    );

    return (
        <div className="bg-white dark:bg-grayDark-950">
            {/* Bandeau campagne influenceur : remplace toute communication
                promo flash tant que la campagne est active */}
            {influencerCampaign && (
                <div className="sticky top-[65px] z-40 bg-primary text-white py-2.5 px-4 text-center text-sm font-medium shadow-md">
                    <span className="font-semibold">
                        {t("pricing_campaign_banner", {
                            label: influencerCampaign.label,
                            percent: campaignPercent,
                        })}
                    </span>
                    <span className="mx-2">·</span>
                    <span>
                        {t("pricing_campaign_until", {
                            date: campaignEndsAtLabel,
                        })}
                    </span>
                </div>
            )}
            {/* Promo banner - sticky below header (65px) */}
            {!isAfricaPricing && PROMO_CONFIG.isActive && promoCountdown && (
                <div className="sticky top-[65px] z-40 bg-yellow-400 text-gray-900 py-2.5 px-4 text-center text-sm font-medium shadow-md">
                    {(locale === "en" ? PROMO_CONFIG.nameEn : PROMO_CONFIG.nameFr) && (
                        <span className="font-black mr-1 uppercase">{locale === "en" ? PROMO_CONFIG.nameEn : PROMO_CONFIG.nameFr}</span>
                    )}
                    <span className="font-semibold">· -{PROMO_CONFIG.discount}% {t("pricing_promo_banner_on_premium")}</span>
                    <span className="mx-2">·</span>
                    <span className="font-mono font-semibold">{t("pricing_promo_ends_in", { time: promoCountdown })}</span>
                </div>
            )}
            <AnimatePresence>
                {modalIsOpen && (
                    <div
                        className="bg-grayDark-950/80 fixed top-0 left-0 bottom-0 right-0 flex justify-center lg:items-center z-[1000]"
                        onClick={(event) => {
                            if (event.target === event.currentTarget) {
                                handleCloseModal();
                            }
                        }}
                    >
                        <motion.div
                            variants={{
                                hidden: { opacity: 0, y: 15 },
                                visible: { opacity: 1, y: 0 },
                            }}
                            initial="hidden"
                            animate="visible"
                            exit="hidden"
                            className="flex lg:flex-row flex-col rounded-xl shadow lg:w-[900px] overflow-y-auto lg:p-0 p-5"
                        >
                            <div className="flex-1 bg-gray-100 p-9 lg:order-1 order-2">
                                {/* Essential : résumé dédié, sans essai */}
                                {type === "essential" ||
                                type === "essential-annually" ? (
                                    <div className="flex flex-col h-full">
                                        <div className="w-12 h-12 bg-primary/15 rounded-2xl flex items-center justify-center text-primary mb-5">
                                            <span className="text-2xl font-bold leading-none">
                                                ∞
                                            </span>
                                        </div>
                                        <h3 className="font-bold text-xl text-gray-900 mb-2">
                                            {t("pricing_essential_tagline")}
                                        </h3>
                                        <p className="text-gray-500 text-sm mb-7">
                                            {t("pricing_essential_description")}
                                        </p>
                                        <div className="space-y-3.5 mb-8">
                                            {[
                                                t("pricing_essential_point1"),
                                                t("pricing_essential_point2"),
                                                ...(repsAssistantIncludedInPlus()
                                                    ? [
                                                          t(
                                                              "pricing_essential_point_bebot",
                                                          ),
                                                      ]
                                                    : []),
                                                ...(isRepsAppStoreLive()
                                                    ? [
                                                          t(
                                                              "pricing_essential_point_app",
                                                          ),
                                                      ]
                                                    : []),
                                                t("pricing_essential_point4"),
                                            ].map((feature, i) => (
                                                <div
                                                    key={i}
                                                    className="flex items-start gap-3"
                                                >
                                                    <SolarIcon
                                                        name="check-circle-bold"
                                                        className="w-5 h-5 text-primary flex-shrink-0 mt-0.5"
                                                    />
                                                    <span className="text-gray-600 text-sm leading-tight">
                                                        {feature}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="mt-auto pt-5 border-t border-gray-200">
                                            <p className="text-gray-500 text-xs mb-1">
                                                {type === "essential-annually"
                                                    ? t("pricing_annual_billing")
                                                    : t(
                                                          "pricing_premium_monthly_only",
                                                      )}
                                            </p>
                                            <p className="text-2xl font-bold text-gray-900 mb-0">
                                                {formatPrice(effectivePrice)}
                                                <span className="text-base font-normal text-gray-500">
                                                    {type === "essential-annually"
                                                        ? t(
                                                              "pricing_yearly_price_euro",
                                                          )
                                                        : t(
                                                              "pricing_monthly_price_euro",
                                                          )}
                                                </span>
                                            </p>
                                        </div>
                                    </div>
                                ) : showTrialUi ? (
                                    <div className="flex flex-col h-full">
                                        <h3 className="font-bold text-lg text-gray-900 mb-8">
                                            {t("pricing_trial_howItWorks")}
                                        </h3>

                                        {/* Timeline */}
                                        <div className="flex-1">
                                            <div className="relative">
                                                {/* Vertical line */}
                                                <div className="absolute left-[15px] top-[30px] bottom-[30px] w-[2px] bg-gradient-to-b from-emerald-400 via-gray-300 to-gray-300" />

                                                {/* Step 1: Create account */}
                                                <div className="flex gap-4 mb-6">
                                                    <div className="relative z-10 w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                                                        {activeSession && activeSession.user ? (
                                                            <SolarIcon
                                                                name="check-circle-bold"
                                                                className="w-4 h-4 text-emerald-600"
                                                            />
                                                        ) : (
                                                            <SolarIcon
                                                                name="user-rounded-bold"
                                                                className="w-4 h-4 text-gray-500"
                                                            />
                                                        )}
                                                    </div>
                                                    <div className="pt-1">
                                                        <p className={`font-semibold mb-0 ${activeSession && activeSession.user ? "line-through text-emerald-600" : "text-gray-900"}`}>
                                                            {activeSession && activeSession.user
                                                                ? t("pricing_trial_step1_title_connected")
                                                                : t("pricing_trial_step1_title_not_connected")
                                                            }
                                                        </p>
                                                        <p className="text-sm text-gray-500 mb-0">
                                                            {activeSession && activeSession.user
                                                                ? t("pricing_trial_step1_description_connected")
                                                                : t("pricing_trial_step1_description_not_connected")
                                                            }
                                                        </p>
                                                    </div>
                                                </div>

                                                {/* Step 2: Today - Get Instant Access */}
                                                <div className="flex gap-4 mb-6">
                                                    <div className="relative z-10 w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                                                        <SolarIcon name="lock-keyhole-minimalistic-unlocked-bold" className="w-4 h-4 text-gray-600" />
                                                    </div>
                                                    <div className="pt-1">
                                                        <p className="font-semibold text-gray-900 mb-0">
                                                            {t("pricing_trial_step2_title")}
                                                        </p>
                                                        <p className="text-sm text-gray-500 mb-0">
                                                            {t("pricing_trial_step2_description")}
                                                        </p>
                                                    </div>
                                                </div>

                                                {/* Step 3: Trial Reminder. Absente d'un essai trop
                                                    court pour avoir un rappel (lib/trialReminder.js) */}
                                                {trialReminderDay > 0 && (
                                                    <div className="flex gap-4 mb-6">
                                                        <div className="relative z-10 w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                                                            <SolarIcon name="bell-bold" className="w-4 h-4 text-gray-600" />
                                                        </div>
                                                        <div className="pt-1">
                                                            <p className="font-semibold text-gray-900 mb-0">
                                                                {t("pricing_trial_step3_title", { day: trialReminderDay })}
                                                            </p>
                                                            <p className="text-sm text-gray-500 mb-0">
                                                                {t("pricing_trial_step3_description")}
                                                            </p>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Step 4: Trial Ends */}
                                                <div className="flex gap-4">
                                                    <div className="relative z-10 w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                                                        <SolarIcon name="star-bold" className="w-4 h-4 text-gray-600" />
                                                    </div>
                                                    <div className="pt-1">
                                                        <p className="font-semibold text-gray-900 mb-0">
                                                            {t("pricing_trial_step4_title", { days: pricingTrial.days })}
                                                        </p>
                                                        <p className="text-sm text-gray-500 mb-0">
                                                            {t("pricing_trial_step4_description", {
                                                                date: moment().add(parseInt(pricingTrial.days), 'days').format(locale && locale.startsWith("en") ? "MM/DD/YYYY" : "DD/MM/YYYY")
                                                            })}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Bottom pricing info */}
                                        <div className="mt-8 pt-6 border-t border-gray-200 text-center">
                                            <p className="text-gray-600 mb-1">
                                                {t("pricing_trial_bottom_text", { days: pricingTrial.days })}
                                            </p>
                                            <p className="font-semibold text-gray-900 mb-0">
                                                {isCheckoutUpfrontAnnual
                                                    ? t("pricing_trial_bottom_price_annual", {
                                                        price: formatPrice(effectivePrice * 12),
                                                        monthlyPrice: formatPrice(effectivePrice)
                                                    })
                                                    : t("pricing_trial_bottom_price_monthly", {
                                                        price: formatPrice(effectivePrice)
                                                    })
                                                }
                                            </p>
                                        </div>
                                    </div>
                                ) : type === "ultra" ? (
                                    /* ============================================
                                       FOUNDER — Apple-inspired layout (compact)
                                       ============================================ */
                                    <div className="relative">
                                        {/* En-tête sobre : mentorat privé, sans
                                            incarnation nominative (délégable) */}
                                        <div className="flex items-center gap-3">
                                            <div className="w-12 h-12 rounded-2xl bg-primary-100/10 flex items-center justify-center text-primary-100/70">
                                                <PixelIcon
                                                    name="crown"
                                                    className="w-6 h-6"
                                                />
                                            </div>
                                            <div>
                                                <p className="text-[10px] font-medium uppercase tracking-[0.25em] text-stone-400 mb-0.5">
                                                    Founder
                                                </p>
                                                <p className="text-stone-900 font-semibold text-lg leading-tight mb-0">
                                                    {t("pricing_founder_mentor_label")}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Promise */}
                                        <div className="mt-6">
                                            <h3 className="text-stone-900 text-[20px] font-semibold leading-[1.2] tracking-tight mb-2">
                                                {t("pricing_founder_promise_title")}
                                            </h3>
                                            <p className="text-stone-500 text-[13px] leading-relaxed mb-0">
                                                {t("pricing_founder_promise_desc")}
                                            </p>
                                        </div>

                                        {/* Inclus — Apple-style features */}
                                        <div className="mt-6 pt-5 border-t border-stone-200">
                                            <p className="text-[10px] font-medium uppercase tracking-[0.2em] text-stone-400 mb-3.5">
                                                {t("pricing_founder_included")}
                                            </p>
                                            <div className="space-y-2.5">
                                                {[
                                                    {
                                                        Icon: SolarCrown,
                                                        label: t("pricing_founder_benefit_premium"),
                                                    },
                                                    {
                                                        Icon: SolarBolt,
                                                        label: t("pricing_founder_benefit_priority"),
                                                    },
                                                    {
                                                        Icon: SolarCalendar,
                                                        label: t("pricing_founder_benefit_booking"),
                                                    },
                                                    {
                                                        Icon: SolarMedalRibbonsStar,
                                                        label: t("pricing_founder_benefit_badge"),
                                                    },
                                                ].map(({ Icon, label }, i) => (
                                                    <div
                                                        key={i}
                                                        className="flex items-center gap-3 text-[13px] text-stone-700 leading-tight"
                                                    >
                                                        <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                                            <Icon className="w-4 h-4 text-primary" />
                                                        </div>
                                                        <span className="mb-0">{label}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Tax breakdown + total with inline /mois */}
                                        <div className="mt-6 pt-5 border-t border-stone-200 space-y-1.5">
                                            <div className="flex justify-between text-[12px] text-stone-500">
                                                <span>{t("pricing_subtotal")}</span>
                                                <span className="font-mono tabular-nums">
                                                    {formatPrice(effectivePrice / 1.2)}€
                                                </span>
                                            </div>
                                            <div className="flex justify-between text-[12px] text-stone-500">
                                                <span>{t("pricing_vat")}</span>
                                                <span className="font-mono tabular-nums">
                                                    {formatPrice(effectivePrice - effectivePrice / 1.2)}€
                                                </span>
                                            </div>
                                            <div className="flex justify-between items-baseline pt-2.5 mt-1 border-t border-stone-200">
                                                <span className="text-[13px] text-stone-900 font-semibold">
                                                    {t("pricing_total")}
                                                </span>
                                                <span className="text-stone-900 font-semibold">
                                                    <span className="text-2xl tracking-tight tabular-nums">{formatPrice(effectivePrice)}</span>
                                                    <span className="text-base text-stone-400 font-normal ml-0.5">€</span>
                                                    <span className="text-[11px] text-stone-400 font-normal ml-1.5">{t("pricing_founder_per_month")}</span>
                                                </span>
                                            </div>
                                        </div>

                                        {/* Reassurance */}
                                        <p className="text-[11px] text-stone-400 text-center mt-5 mb-0 leading-relaxed">
                                            {t("pricing_founder_cancel_anytime")}
                                        </p>
                                    </div>
                                ) : (
                                    /* Original order summary for non-trial (Premium, Africa, etc.) */
                                    <>
                                        <h3 className="font-bold opacity-40">
                                            {t("pricing_yourOrder")}
                                        </h3>
                                        <div className="text-3xl text-gray-900 font-semibold mt-4">
                                            {formatPrice(
                                                isCheckoutUpfrontAnnual
                                                    ? effectivePrice * 12
                                                    : effectivePrice,
                                            )}
                                            <span className="text-lg">
                                                € {t("Call_inclVAT")}
                                            </span>
                                            {type === WINBACK_CHECKOUT_TYPE && (
                                                <del className="ml-3 text-lg font-normal text-gray-400">
                                                    {formatPrice(
                                                        process.env.BELIEVEMY_PREMIUM_MONTHLY_PRICE,
                                                    )}
                                                    €
                                                </del>
                                            )}
                                        </div>
                                        <div className="flex lg:flex-row flex-col gap-5 lg:mt-5">
                                            <div>
                                                <Image
                                                    src="/images/site/brand/icon.png"
                                                    alt="app"
                                                    className="w-10 lg:block hidden"
                                                    width={30}
                                                    height={30}
                                                />
                                            </div>
                                            <div className="w-full">
                                                <div className="text-lg">
                                                    {type == "ultra"
                                                        ? t("pricing_believemyUltra")
                                                        : type == "africa-committed"
                                                        ? t("pricing_believemyPremiumAfricaCommitted")
                                                        : type == "africa-monthly"
                                                        ? t("pricing_believemyPremiumAfricaMonthly")
                                                        : type == "annually"
                                                        ? t(
                                                              "pricing_believemyPremiumAnnually",
                                                          )
                                                        : t("pricing_believemyPremium")}
                                                </div>
                                                <div className="text-sm mt-1 text-gray-500">
                                                    {(type == "ultra" ||
                                                        type == "monthly" ||
                                                        type == WINBACK_CHECKOUT_TYPE ||
                                                        type == "africa-monthly" ||
                                                        (type == "annually" && !annuallyIsMonthlyBilling) ||
                                                        (type == "africa-committed" && !africaCommittedIsMonthlyBilling)) &&
                                                        t("pricing_noEngagement")}
                                                    {type == "6-months" &&
                                                        t("pricing_sixEngagement")}
                                                    {type == "africa-committed" && africaCommittedIsMonthlyBilling && (
                                                        <div className="flex items-center justify-between text-xs opacity-90">
                                                            <div>
                                                                {t(
                                                                    "pricing_africa_year_engagement",
                                                                    {
                                                                        pricePerMonth:
                                                                            formatPrice(
                                                                                price,
                                                                            ),
                                                                    },
                                                                )}
                                                            </div>
                                                            <div className="text-xs text-gray-500">
                                                                {t(
                                                                    "pricing_africa_year_engagement_description",
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                    {type == "annually" && annuallyIsMonthlyBilling && (
                                                        <div className="flex items-center justify-between text-xs opacity-90">
                                                            <div>
                                                                {t(
                                                                    "pricing_yearEngagement",
                                                                    {
                                                                        pricePerMonth:
                                                                            formatPrice(
                                                                                price,
                                                                            ),
                                                                    },
                                                                )}
                                                            </div>
                                                            <div className="text-xs text-gray-500">
                                                                {t(
                                                                    "pricing_yearEngagementDescription",
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                                {/* Offre de retour : la remise se lit
                                                    ligne à ligne, prix habituel puis
                                                    ce qu'on en retire */}
                                                {type === WINBACK_CHECKOUT_TYPE && (
                                                    <div className="border-t pt-5 mt-5 text-sm">
                                                        <div className="flex justify-between items-center text-gray-500">
                                                            <div className="font-semibold">
                                                                {t("winback_checkout_regular")}
                                                            </div>
                                                            <div>
                                                                {formatPrice(
                                                                    process.env.BELIEVEMY_PREMIUM_MONTHLY_PRICE,
                                                                )}
                                                                €
                                                            </div>
                                                        </div>
                                                        <div className="flex mt-1 justify-between items-center gap-4 text-primary">
                                                            <div className="font-semibold">
                                                                {t("winback_checkout_discount")}
                                                            </div>
                                                            <div className="whitespace-nowrap">
                                                                -
                                                                {formatPrice(
                                                                    parseFloat(
                                                                        String(
                                                                            process.env.BELIEVEMY_PREMIUM_MONTHLY_PRICE,
                                                                        ).replace(",", "."),
                                                                    ) - price,
                                                                )}
                                                                €
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                                <div className="border-t pt-5 flex mt-5 justify-between items-center text-sm text-gray-500">
                                                    <div className="font-semibold">
                                                        {t("pricing_subtotal")}
                                                    </div>
                                                    <div>
                                                        {formatPrice(
                                                            isCheckoutUpfrontAnnual
                                                                ? (effectivePrice * 12) / 1.2
                                                                : effectivePrice / 1.2,
                                                        )}
                                                        €
                                                    </div>
                                                </div>

                                                <div className="flex mt-1 justify-between items-center text-sm text-gray-500">
                                                    <div className="font-semibold">
                                                        {t("pricing_vat")}
                                                    </div>
                                                    <div>
                                                        {formatPrice(
                                                            isCheckoutUpfrontAnnual
                                                                ? effectivePrice * 12 -
                                                                      (effectivePrice * 12) / 1.2
                                                                : effectivePrice -
                                                                      effectivePrice / 1.2,
                                                        )}
                                                        €{/* Calcul après fact. */}
                                                    </div>
                                                </div>

                                                <div className="border-t pt-5 flex mt-5 justify-between items-center text-sm">
                                                    <div className="font-semibold">
                                                        {t("pricing_total")}
                                                    </div>
                                                    <div>
                                                        {formatPrice(
                                                            isCheckoutUpfrontAnnual
                                                                ? effectivePrice * 12
                                                                : effectivePrice,
                                                        )}
                                                        €
                                                    </div>
                                                </div>

                                                {campaignAppliesToType && (
                                                    <p className="mt-2 text-xs text-gray-400 mb-0">
                                                        {isCheckoutUpfrontAnnual
                                                            ? t(
                                                                  "pricing_offer_year_line",
                                                                  {
                                                                      price: formatPrice(
                                                                          effectivePrice *
                                                                              12,
                                                                      ),
                                                                      original:
                                                                          formatPrice(
                                                                              price *
                                                                                  12,
                                                                          ),
                                                                      renewal: formatPrice(price),
                                                                  },
                                                              )
                                                            : t(
                                                                  "pricing_offer_monthly_line",
                                                                  {
                                                                      price: formatPrice(
                                                                          effectivePrice,
                                                                      ),
                                                                      renewal: formatPrice(
                                                                          price,
                                                                      ),
                                                                  },
                                                              )}
                                                    </p>
                                                )}
                                                {type === WINBACK_CHECKOUT_TYPE && (
                                                    <p className="mt-2 text-xs text-gray-400 mb-0">
                                                        {t("winback_checkout_line", {
                                                            price: formatPrice(price),
                                                            date: moment()
                                                                .add(1, "month")
                                                                .locale(locale)
                                                                .format("LL"),
                                                            renewal: formatPrice(
                                                                process.env.BELIEVEMY_PREMIUM_MONTHLY_PRICE,
                                                            ),
                                                        })}
                                                    </p>
                                                )}
                                                {PROMO_CONFIG.isActive &&
                                                    type == "annually" && (
                                                        <p className="mt-2 text-xs text-gray-400 mb-0">
                                                            {isCheckoutUpfrontAnnual
                                                                ? t(
                                                                      "pricing_offer_year_line",
                                                                      {
                                                                          price: formatPrice(
                                                                              price *
                                                                                  12,
                                                                          ),
                                                                          original:
                                                                              formatPrice(
                                                                                  PROMO_CONFIG.prices.getAnnualBaseTotalPrice(),
                                                                              ),
                                                                          renewal: formatPrice(
                                                                              PROMO_CONFIG.prices.getAnnualBaseTotalPrice() /
                                                                                  12,
                                                                          ),
                                                                      },
                                                                  )
                                                                : t(
                                                                      "pricing_offer_monthly_line",
                                                                      {
                                                                          price: formatPrice(
                                                                              price,
                                                                          ),
                                                                          renewal: formatPrice(
                                                                              PROMO_CONFIG
                                                                                  .prices
                                                                                  .annuallyBase,
                                                                          ),
                                                                      },
                                                                  )}
                                                        </p>
                                                    )}

                                                {/* Free call bonus */}
                                                {FREE_CALL_CONFIG.isActive &&
                                                    type === "annually" && (
                                                        <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                                                            <div className="flex items-center gap-2">
                                                                <div className="w-6 h-6 bg-emerald-100 rounded-md flex items-center justify-center">
                                                                    <SolarIcon
                                                                        name="phone-rounded-bold"
                                                                        className="w-3.5 h-3.5 text-emerald-600"
                                                                    />
                                                                </div>
                                                                <div className="flex-1">
                                                                    <p className="text-emerald-700 text-xs font-semibold mb-0">
                                                                        {t("pricing_free_call_bonus")}
                                                                    </p>
                                                                    <p className="text-emerald-600/70 text-xs mb-0">
                                                                        {t("pricing_free_call_with")}
                                                                    </p>
                                                                </div>
                                                                <div className="text-emerald-600 text-xs font-semibold">
                                                                    {t("pricing_free")}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}

                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                            <div className="flex-1 bg-white p-9 relative text-sm lg:order-2 order-1">
                                <div className="absolute top-5 right-5 z-10">
                                    <button
                                        type="button"
                                        onClick={handleCloseModal}
                                        aria-label={t("pricing_close_checkout")}
                                        className="flex h-9 w-9 items-center justify-center rounded-full text-stone-400 hover:text-stone-600 hover:bg-stone-100 transition-colors"
                                    >
                                        <SolarIcon
                                            name="close-circle-bold"
                                            className="w-7 h-7"
                                        />
                                    </button>
                                </div>
                                {clientSecret ? (
                                    <>
                                        <div className="mb-5">
                                            <h3 className="text-stone-900 text-lg font-semibold leading-tight mb-2">
                                                {t("pricing_bankingInfos")}
                                            </h3>
                                            <p className="text-stone-500 text-[13px] leading-relaxed mb-0">
                                                {t(
                                                    "pricing_bankingInfosDescription",
                                                )}
                                            </p>
                                        </div>
                                        {/* Rassurer juste avant la carte : c'est une
                                            bonne nouvelle, pas une alerte. Encadré vert
                                            pâle et texte posé, plus d'orange gras qui se
                                            lisait comme une erreur */}
                                        {(type === "ultra" || actualIsTrial) && (
                                            <div className="mb-4 flex items-start gap-2.5 rounded-[10px] border border-green-200 bg-green-50 px-3 py-2.5">
                                                <SolarShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-600" />
                                                <p className="mb-0 text-[13px] leading-relaxed text-stone-700">
                                                    {type === "ultra"
                                                        ? t("pricing_founder_no_engagement")
                                                        : t("pricing_youCanStopAnytime")}
                                                </p>
                                            </div>
                                        )}
                                        {/* Payer pour une entreprise après coup (ou
                                            corriger celle enregistrée) : la tentative
                                            est annulée et le formulaire revient */}
                                        {activeSession?.user && (
                                            <button
                                                type="button"
                                                onClick={onSwitchToCompanyForm}
                                                className="mb-4 flex items-center gap-1.5 text-left text-[13px] font-medium text-stone-500 transition-colors hover:text-stone-800"
                                            >
                                                <SolarIcon
                                                    name="buildings-2-linear"
                                                    className="h-4 w-4 shrink-0"
                                                />
                                                <span className="underline decoration-stone-300 underline-offset-4">
                                                    {companyEnabled && company.name
                                                        ? t("billing_company_invoiced_to", {
                                                              name: company.name,
                                                          })
                                                        : t("billing_company_toggle")}
                                                </span>
                                            </button>
                                        )}
                                        <Elements
                                            key={clientSecret}
                                            stripe={loadStripe(
                                                process.env.STRIPE_PUBLIC_KEY,
                                            )}
                                            options={{
                                                clientSecret,
                                                locale: locale,
                                            }}
                                        >
                                            <CheckoutForm
                                                trial={actualIsTrial === true}
                                                type={type}
                                                payLabel={
                                                    type === WINBACK_CHECKOUT_TYPE
                                                        ? t("winback_pay_button", {
                                                              price: formatPrice(price),
                                                          })
                                                        : undefined
                                                }
                                                clientSecret={clientSecret}
                                                subscriptionId={
                                                    pendingSubscriptionId
                                                }
                                                price={price}
                                                billingAddress={{
                                                    country:
                                                        billingAddress.addressCountry,
                                                    postalCode:
                                                        billingAddress.addressPostal,
                                                }}
                                            />
                                        </Elements>
                                    </>
                                ) : preparingPayment ? (
                                    <div className="flex h-full min-h-[320px] flex-col items-center justify-center gap-4 text-center">
                                        <SpinnerDotted
                                            size={44}
                                            thickness={120}
                                            color="#E8511D"
                                        />
                                        <p className="text-stone-500 text-sm mb-0">
                                            {t("pricing_preparing_payment")}
                                        </p>
                                    </div>
                                ) : (
                                    <>
                                        <div className="mb-5">
                                            <h3 className="text-stone-900 text-base font-semibold tracking-[-0.01em] leading-tight mb-1">
                                                {activeSession?.user
                                                    ? t("pricing_billingInfos")
                                                    : t("pricing_checkout_title")}
                                            </h3>
                                            <p className="text-stone-500 text-[13px] leading-relaxed mb-0">
                                                {activeSession?.user
                                                    ? t(
                                                          "pricing_billingInfosDescription",
                                                      )
                                                    : t(
                                                          "pricing_checkout_description",
                                                      )}
                                            </p>
                                        </div>

                                        <form
                                            onSubmit={(e) => {
                                                e.preventDefault();
                                                if (loading) return;
                                                if (showRecaptchaV2) {
                                                    handleRecaptchaV2Submit();
                                                } else {
                                                    onSubmitCheckout();
                                                }
                                            }}
                                        >
                                            {!activeSession?.user && (
                                                <>
                                                    <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-3">
                                                        <div className="group">
                                                            <label
                                                                className={
                                                                    CHECKOUT_LABEL_CLASS
                                                                }
                                                                htmlFor="email"
                                                            >
                                                                {t(
                                                                    "pricing_email",
                                                                )}
                                                            </label>
                                                            <input
                                                                type="email"
                                                                autoComplete="email"
                                                                spellCheck={
                                                                    false
                                                                }
                                                                autoCapitalize="none"
                                                                ref={
                                                                    emailInputRef
                                                                }
                                                                className={
                                                                    CHECKOUT_INPUT_CLASS
                                                                }
                                                                id="email"
                                                                placeholder={t(
                                                                    "pricing_email_placeholder",
                                                                )}
                                                                value={
                                                                    emailAddress
                                                                }
                                                                onChange={(e) => {
                                                                    setEmailAddress(
                                                                        e.target
                                                                            .value,
                                                                    );
                                                                    // L'email change : la création de compte en
                                                                    // attente (fallback v2) n'est plus valable
                                                                    if (showRecaptchaV2) {
                                                                        setShowRecaptchaV2(
                                                                            false,
                                                                        );
                                                                        setPendingCreateUser(
                                                                            null,
                                                                        );
                                                                        setRecaptchaV2Token(
                                                                            null,
                                                                        );
                                                                    }
                                                                }}
                                                            />
                                                        </div>
                                                        <div className="group">
                                                            <label
                                                                className={
                                                                    CHECKOUT_LABEL_CLASS
                                                                }
                                                                htmlFor="password"
                                                            >
                                                                {t(
                                                                    "pricing_password",
                                                                )}
                                                            </label>
                                                            <div className="relative">
                                                                <input
                                                                    type={
                                                                        showPassword
                                                                            ? "text"
                                                                            : "password"
                                                                    }
                                                                    autoComplete="current-password"
                                                                    className={
                                                                        CHECKOUT_INPUT_CLASS +
                                                                        " pr-10"
                                                                    }
                                                                    id="password"
                                                                    placeholder="••••••••"
                                                                    value={
                                                                        password
                                                                    }
                                                                    onChange={(e) =>
                                                                        setPassword(
                                                                            e.target
                                                                                .value,
                                                                        )
                                                                    }
                                                                />
                                                                <button
                                                                    type="button"
                                                                    onClick={() =>
                                                                        setShowPassword(
                                                                            (v) =>
                                                                                !v,
                                                                        )
                                                                    }
                                                                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 transition-colors"
                                                                    aria-label={t(
                                                                        "pricing_toggle_password",
                                                                    )}
                                                                >
                                                                    <SolarIcon
                                                                        name={
                                                                            showPassword
                                                                                ? "eye-closed-linear"
                                                                                : "eye-linear"
                                                                        }
                                                                        className="w-[18px] h-[18px]"
                                                                    />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {/* Pas de rappel "déjà un compte" ici :
                                                        le sous-titre de la modale le dit déjà */}
                                                    <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-3">
                                                        <div className="group">
                                                            <label
                                                                htmlFor="firstName"
                                                                className={
                                                                    CHECKOUT_LABEL_CLASS
                                                                }
                                                            >
                                                                {t(
                                                                    "pricing_firstName",
                                                                )}
                                                            </label>
                                                            <input
                                                                type="text"
                                                                autoComplete="given-name"
                                                                className={
                                                                    CHECKOUT_INPUT_CLASS
                                                                }
                                                                id="firstName"
                                                                placeholder={t(
                                                                    "pricing_firstName_placeholder",
                                                                )}
                                                                value={
                                                                    firstName
                                                                }
                                                                onChange={(e) =>
                                                                    setFirstName(
                                                                        e.target
                                                                            .value,
                                                                    )
                                                                }
                                                            />
                                                        </div>
                                                        <div className="group">
                                                            <label
                                                                htmlFor="lastName"
                                                                className={
                                                                    CHECKOUT_LABEL_CLASS
                                                                }
                                                            >
                                                                {t(
                                                                    "pricing_lastName",
                                                                )}
                                                            </label>
                                                            <input
                                                                type="text"
                                                                autoComplete="family-name"
                                                                className={
                                                                    CHECKOUT_INPUT_CLASS
                                                                }
                                                                id="lastName"
                                                                placeholder={t(
                                                                    "pricing_lastName_placeholder",
                                                                )}
                                                                value={
                                                                    lastName
                                                                }
                                                                onChange={(e) =>
                                                                    setLastName(
                                                                        e.target
                                                                            .value,
                                                                    )
                                                                }
                                                            />
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                            {/* Particulier (par défaut) : facturation minimale, le
                                                pays (prérempli par la géo) puis le code postal si le
                                                pays en utilise un. Stripe Tax ne l'exige qu'aux
                                                États-Unis et au Canada : facultatif ailleurs, masqué
                                                là où il n'existe pas (une grande partie de l'Afrique).
                                                Entreprise : le bloc adresse devient le sien, pays et
                                                code postal compris, pour une facture complète */}
                                            <div className="mt-4">
                                                <BillingModeSwitch
                                                    tone="light"
                                                    company={companyEnabled}
                                                    onChange={(isCompany) => {
                                                        if (isCompany === companyEnabled) return;
                                                        // Revenir à « Particulier » rend son
                                                        // propre pays et son code postal
                                                        if (isCompany) {
                                                            personalLocationRef.current = {
                                                                country: addressCountry,
                                                                postal: addressPostal,
                                                            };
                                                        } else if (personalLocationRef.current) {
                                                            setAddressCountry(
                                                                personalLocationRef.current.country,
                                                            );
                                                            setAddressPostal(
                                                                personalLocationRef.current.postal,
                                                            );
                                                        }
                                                        setCompanyEnabled(isCompany);
                                                        setFormError(null);
                                                    }}
                                                />
                                            </div>
                                            {!companyEnabled && (
                                            <div
                                                className={`mt-4 grid grid-cols-1 gap-4 sm:gap-3 ${
                                                    postalCodeField.visible ? "sm:grid-cols-2" : ""
                                                }`}
                                            >
                                                <div className="group">
                                                    <label
                                                        htmlFor="addressCountry"
                                                        className={CHECKOUT_LABEL_CLASS}
                                                    >
                                                        {t("pricing_country")}
                                                    </label>
                                                    {/* Select natif (236 pays, triés dans la langue
                                                        du visiteur) habillé : appearance-none +
                                                        chevron Solar */}
                                                    <div className="relative">
                                                        <select
                                                            name="addressCountry"
                                                            id="addressCountry"
                                                            autoComplete="country"
                                                            className={CHECKOUT_SELECT_CLASS}
                                                            onChange={(e) => {
                                                                setAddressCountry(e.target.value);
                                                                setFormError(null);
                                                            }}
                                                            value={addressCountry}
                                                        >
                                                            {countryOptions.map((country) => (
                                                                <option
                                                                    key={country.code}
                                                                    value={country.code}
                                                                >
                                                                    {country.label}
                                                                </option>
                                                            ))}
                                                        </select>
                                                        <SolarIcon
                                                            name="alt-arrow-down-linear"
                                                            className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400"
                                                        />
                                                    </div>
                                                </div>
                                                {postalCodeField.visible && (
                                                    <div className="group">
                                                        <label
                                                            htmlFor="addressPostal"
                                                            className={CHECKOUT_LABEL_CLASS}
                                                        >
                                                            {t(postalCodeField.labelKey)}
                                                        </label>
                                                        <input
                                                            type="text"
                                                            autoComplete="postal-code"
                                                            inputMode={postalCodeField.inputMode}
                                                            autoCapitalize={
                                                                postalCodeField.autoCapitalize
                                                            }
                                                            spellCheck={false}
                                                            className={CHECKOUT_INPUT_CLASS}
                                                            id="addressPostal"
                                                            placeholder={postalCodeField.example}
                                                            value={addressPostal}
                                                            onChange={(e) => {
                                                                setAddressPostal(e.target.value);
                                                                setFormError(null);
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                            )}
                                            <AnimatePresence initial={false}>
                                                {companyEnabled && (
                                                    <motion.div
                                                        initial={{ opacity: 0, y: 8 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        exit={{ opacity: 0 }}
                                                        transition={{
                                                            duration: 0.2,
                                                            ease: "easeOut",
                                                        }}
                                                        className="mt-4"
                                                    >
                                                        <CompanyBillingFields
                                                            idPrefix="checkout-company"
                                                            tone="light"
                                                            value={{
                                                                ...company,
                                                                country: addressCountry,
                                                                postalCode: addressPostal,
                                                            }}
                                                            onChange={(patch) => {
                                                                // Pays et code postal restent ceux du
                                                                // tunnel (TVA, essai) : ce sont ceux de
                                                                // l'entreprise en mode entreprise
                                                                const {
                                                                    country,
                                                                    postalCode,
                                                                    ...rest
                                                                } = patch;
                                                                if (country !== undefined) {
                                                                    setAddressCountry(country);
                                                                }
                                                                if (postalCode !== undefined) {
                                                                    setAddressPostal(postalCode);
                                                                }
                                                                if (Object.keys(rest).length) {
                                                                    setCompany((current) => ({
                                                                        ...current,
                                                                        ...rest,
                                                                    }));
                                                                }
                                                                setFormError(null);
                                                            }}
                                                            labelClassName={
                                                                CHECKOUT_LABEL_CLASS
                                                            }
                                                            inputClassName={
                                                                CHECKOUT_INPUT_CLASS
                                                            }
                                                            selectClassName={
                                                                CHECKOUT_SELECT_CLASS
                                                            }
                                                        />
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>

                                            {/* Erreur de validation inline */}
                                            {formError && (
                                                <p
                                                    className="mt-4 text-[13px] font-medium text-red-600 mb-0"
                                                    role="alert"
                                                >
                                                    {formError}
                                                </p>
                                            )}

                                            {/* reCAPTCHA v2 fallback */}
                                            {showRecaptchaV2 && (
                                                <div className="mt-4">
                                                    <p className="text-sm text-stone-500 mb-3">
                                                        {t(
                                                            "signup_recaptcha_verify",
                                                        )}
                                                    </p>
                                                    <ReCAPTCHA
                                                        ref={recaptchaV2Ref}
                                                        sitekey={
                                                            process.env
                                                                .NEXT_PUBLIC_RECAPTCHA_V2_SITE_KEY
                                                        }
                                                        onChange={
                                                            handleRecaptchaV2Change
                                                        }
                                                    />
                                                </div>
                                            )}

                                            {/* Un seul bouton, toujours visible :
                                                désactivé tant que le captcha v2
                                                n'est pas résolu. Pleine largeur :
                                                zone de clic maximale, aligné sur
                                                les champs */}
                                            <div className="mt-6">
                                                <Button
                                                    className="w-full"
                                                    disabled={
                                                        loading ||
                                                        (showRecaptchaV2 &&
                                                            !recaptchaV2Token)
                                                    }
                                                    isLoading={loading}
                                                    progress
                                                    type="primary"
                                                >
                                                    {t("pricing_continue")}
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        width="1em"
                                                        height="1em"
                                                        viewBox="0 0 24 24"
                                                        className="ml-3"
                                                    >
                                                        <path
                                                            fill="currentColor"
                                                            fillRule="evenodd"
                                                            d="M13.47 5.47a.75.75 0 0 1 1.06 0l6 6a.75.75 0 0 1 0 1.06l-6 6a.75.75 0 1 1-1.06-1.06l4.72-4.72H4a.75.75 0 0 1 0-1.5h14.19l-4.72-4.72a.75.75 0 0 1 0-1.06"
                                                            clipRule="evenodd"
                                                        ></path>
                                                    </svg>
                                                </Button>
                                            </div>
                                            <p className="mt-3 flex items-center justify-center gap-1.5 text-[12px] text-stone-400 mb-0">
                                                <SolarIcon
                                                    name="lock-keyhole-minimalistic-linear"
                                                    className="w-3.5 h-3.5"
                                                />
                                                {t("pricing_secure_payment")}
                                            </p>
                                        </form>
                                    </>
                                )}
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
            {content}
            <Footer withoutMarginTop />
        </div>
    );
}

// Wrapper component with GoogleReCaptchaProvider
export default function PricingComponent({ session, ...props }) {
    return (
        <GoogleReCaptchaProvider
            reCaptchaKey={process.env.NEXT_PUBLIC_RECAPTCHA_V3_SITE_KEY}
            scriptProps={{
                async: true,
                defer: true,
            }}
        >
            <PricingComponentInner session={session} {...props} />
            {/* Chat chargé seulement quand le visiteur l'ouvre (cookies de Crisp) */}
            <CrispChatbox />
        </GoogleReCaptchaProvider>
    );
}
